import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Grid,
  Typography,
  Divider,
  TextField,
  IconButton,
  Button,
  Stack,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Collapse,
  Tabs,
  Tab,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useState, forwardRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import CircularProgress from "@mui/material/CircularProgress";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import EmailIcon from "@mui/icons-material/Email";
import EditNoteIcon from "@mui/icons-material/EditNote";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SecurityIcon from "@mui/icons-material/Security";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DescriptionIcon from "@mui/icons-material/Description";
import PhoneIcon from "@mui/icons-material/Phone";
import HistoryIcon from "@mui/icons-material/History";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssessmentIcon from "@mui/icons-material/Assessment";
import RefreshIcon from "@mui/icons-material/Refresh";
import Chip from "@mui/material/Chip";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Labels from "../labels/Labels";
import UserService from "../services/UserService";
import KycDocumentsViewer from "./KycDocumentsViewer";
import AuditHistoryNoteForm from "./AuditHistoryNoteForm";
import {
  formatLocalDateTime,
  formatRelativeTime,
  parseDatabaseDate,
} from "../util/dateTime";

export default function MemberSecureInfo(props) {
  const [secureInfoData, setSecureInfoData] = useState({});
  const [relativeTimeNow, setRelativeTimeNow] = useState(() => Date.now());
  const { memberInfo } = props;
  const queryClient = useQueryClient();

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  // Edit mode states
  const [isEditingDOB, setIsEditingDOB] = useState(false);
  const [isEditingIdentification, setIsEditingIdentification] = useState(false);
  const [showMoreIdentifications, setShowMoreIdentifications] = useState(false);

  // KYC document preview state
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [previewFileName, setPreviewFileName] = useState(null);
  const [previewDownloadSrc, setPreviewDownloadSrc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const intervalId = setInterval(
      () => setRelativeTimeNow(Date.now()),
      60 * 1000
    );

    return () => clearInterval(intervalId);
  }, []);

  const DOCUMENT_TYPE_LABELS = {
    1: "SSN",
    8: "ITIN",
    PASSPORT: "Passport",
    DRIVERS_LICENSE: "Driver's License",
    COUNTRY_ID: "Country ID",
    STATE_ID: "State ID",
    PROOF_OF_ADDRESS: "Proof of Address",
    SSN_CARD: "SSN Card",
    ITIN_LETTER: "ITIN Letter",
  };

  const getIdentificationTypeLabel = (documentType) => {
    if (documentType === null || documentType === undefined) return null;
    const normalized = String(documentType).trim();
    if (!normalized) return null;
    return DOCUMENT_TYPE_LABELS[normalized] || normalized;
  };

  const getIdentificationJurisdiction = (item) => {
    if (!item || !item.documentType) return null;

    if (item.documentType === "COUNTRY_ID") {
      return item.countryCode || item.country || null;
    }

    if (item.documentType === "STATE_ID") {
      return (
        item.stateCode ||
        item.state ||
        item.issuingState ||
        item.countryCode ||
        null
      );
    }

    return null;
  };

  const extensionFromMimeType = (mimeType) => {
    const normalized = (mimeType || "").toLowerCase();
    if (normalized.includes("pdf")) return "pdf";
    if (normalized.includes("jpeg") || normalized.includes("jpg")) return "jpg";
    if (normalized.includes("png")) return "png";
    if (normalized.includes("webp")) return "webp";
    return "bin";
  };

  async function handleViewDocument(doc, original = false) {
    setPreviewDoc(doc);
    setPreviewSrc(null);
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const res = await HttpService.getAxiosClient().get(
        window.API_URL +
          `/member/kyc-documents/${doc.id}/download?companyId=${memberInfo?.companyId}&original=${original}`,
        { responseType: "blob" }
      );
      const sniffMimeType = async (blob) => {
        const buffer = await blob.slice(0, 16).arrayBuffer();
        const bytes = new Uint8Array(buffer);

        // PDF: %PDF
        if (
          bytes.length >= 4 &&
          bytes[0] === 0x25 &&
          bytes[1] === 0x50 &&
          bytes[2] === 0x44 &&
          bytes[3] === 0x46
        ) {
          return "application/pdf";
        }

        // JPEG: FF D8 FF
        if (
          bytes.length >= 3 &&
          bytes[0] === 0xff &&
          bytes[1] === 0xd8 &&
          bytes[2] === 0xff
        ) {
          return "image/jpeg";
        }

        // PNG: 89 50 4E 47 0D 0A 1A 0A
        if (
          bytes.length >= 8 &&
          bytes[0] === 0x89 &&
          bytes[1] === 0x50 &&
          bytes[2] === 0x4e &&
          bytes[3] === 0x47 &&
          bytes[4] === 0x0d &&
          bytes[5] === 0x0a &&
          bytes[6] === 0x1a &&
          bytes[7] === 0x0a
        ) {
          return "image/png";
        }

        // WEBP: RIFF....WEBP
        if (
          bytes.length >= 12 &&
          bytes[0] === 0x52 &&
          bytes[1] === 0x49 &&
          bytes[2] === 0x46 &&
          bytes[3] === 0x46 &&
          bytes[8] === 0x57 &&
          bytes[9] === 0x45 &&
          bytes[10] === 0x42 &&
          bytes[11] === 0x50
        ) {
          return "image/webp";
        }

        return null;
      };

      const serverMimeType = (res?.headers?.["content-type"] || "")
        .toLowerCase()
        .split(";")[0]
        .trim();
      const sniffedMimeType = await sniffMimeType(res.data);

      // Trust byte signature first. If unknown, only trust explicit PDF from server.
      const normalizedMimeType =
        sniffedMimeType ||
        (serverMimeType.includes("pdf")
          ? "application/pdf"
          : "application/octet-stream");

      const typedBlob = new Blob([res.data], {
        type: normalizedMimeType || "application/octet-stream",
      });
      const fallbackName = `${doc?.documentType || "document"}_${doc?.side || "file"}.${extensionFromMimeType(normalizedMimeType)}`;
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(typedBlob);
      });
      const objectUrl = window.URL.createObjectURL(typedBlob);

      setPreviewDoc({
        ...doc,
        mimeType: normalizedMimeType || doc?.mimeType,
        original,
      });
      setPreviewSrc(dataUrl);
      setPreviewDownloadSrc(objectUrl);
      setPreviewFileName(
        original
          ? `${doc?.documentType || "document"}_${doc?.side || "file"}_original.${extensionFromMimeType(normalizedMimeType)}`
          : doc?.originalFileName || fallbackName
      );
    } catch (err) {
      setPreviewError("Failed to load document preview.");
    } finally {
      setPreviewLoading(false);
    }
  }

  function closePreview() {
    if (previewDownloadSrc && previewDownloadSrc.startsWith("blob:")) {
      window.URL.revokeObjectURL(previewDownloadSrc);
    }
    setPreviewDoc(null);
    setPreviewSrc(null);
    setPreviewFileName(null);
    setPreviewDownloadSrc(null);
    setPreviewError(null);
    setPreviewLoading(false);
  }

  // Temporary values for editing
  const [tempDateOfBirth, setTempDateOfBirth] = useState("");
  const [tempIdentification, setTempIdentification] = useState("");

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  const dedupeQualifiers = (items = []) => {
    const seen = new Set();
    return (Array.isArray(items) ? items : []).filter((item) => {
      const keyPart = (item?.key || "").toString().trim().toLowerCase();
      const messagePart = (item?.message || "").toString().trim().toLowerCase();
      const fingerprint = `${keyPart}|${messagePart}`;
      if (seen.has(fingerprint)) {
        return false;
      }
      seen.add(fingerprint);
      return Boolean(keyPart || messagePart);
    });
  };

  // Parse KYC_SCAN_RESULT notes field (Java object string format)
  const parseKycScanResult = (notesStr) => {
    try {
      const normalizeValue = (value) => {
        if (value === null || value === undefined) return null;
        const text = String(value).trim();
        if (!text) return null;
        const lowered = text.toLowerCase();
        return lowered === "null" || lowered === "undefined" ? null : text;
      };

      // Extract key sections from the Java object string
      const extractField = (pattern, defaultVal = null) => {
        const regex = new RegExp(pattern);
        const match = notesStr.match(regex);
        return normalizeValue(match ? match[1] : defaultVal);
      };

      const parseResultDetail = (prefix) => {
        const resultMatch = notesStr.match(
          new RegExp(
            `${prefix}=ResultDetail\\(key=([^,]+),\\s*message=([^)]+)\\)`
          )
        );
        if (!resultMatch) return null;

        const key = normalizeValue(resultMatch[1]);
        const message = normalizeValue(resultMatch[2]);
        return key || message ? { key, message } : null;
      };

      const parseLocatedIdScanRecord = () => {
        const locatedMatch = notesStr.match(
          /locatedIdScanRecord=LocatedIdScanRecord\(([\s\S]*?)\),\s*additionalScanDetails=/
        );
        const locatedStr = locatedMatch?.[1];
        if (!locatedStr) return null;

        const extractLocatedField = (pattern) => {
          const match = locatedStr.match(new RegExp(pattern));
          return normalizeValue(match ? match[1] : null);
        };

        const dateFromLocated = (fieldName) => {
          const match = locatedStr.match(
            new RegExp(
              `${fieldName}=IdScanDate\\(year=(\\d+),\\s*month=(\\d+)\\)`
            )
          );
          return match ? `${match[2].padStart(2, "0")}/${match[1]}` : null;
        };

        const locatedIdScanRecord = {
          firstName: extractLocatedField(/idScanFirstName=([^,)]+)/),
          middleName: extractLocatedField(/idScanMiddleName=([^,)]+)/),
          lastName: extractLocatedField(/idScanLastName=([^,)]+)/),
          streetNumber: extractLocatedField(/idScanStreetNumber=([^,)]+)/),
          streetName: extractLocatedField(/idScanStreetName=([^,)]+)/),
          city: extractLocatedField(/idScanCity=([^,)]+)/),
          zip: extractLocatedField(/idScanZip=([^,)]+)/),
          state: extractLocatedField(/idScanState=([^,)]+)/),
          country: extractLocatedField(/idScanCountry=([^,)]+)/),
          issuanceState: extractLocatedField(/idScanIssuanceState=([^,)]+)/),
          documentNumber: extractLocatedField(/idScanDocumentNumber=([^,)]+)/),
          documentType: extractLocatedField(/idScanDocumentType=([^,)]+)/),
          templateType: extractLocatedField(/idScanTemplateType=([^,)]+)/),
          confidenceScore: extractLocatedField(
            /idScanConfidenceScore=([\d.]+)/
          ),
          dateOfBirth: dateFromLocated("idScanDateOfBirth"),
          dateOfIssuance: dateFromLocated("idScanDateOfIssuance"),
          expirationDate: dateFromLocated("idScanExpirationDate"),
        };

        const hasAnyValue = Object.values(locatedIdScanRecord).some(Boolean);
        return hasAnyValue ? locatedIdScanRecord : null;
      };

      // Extract overall result
      const overallResult = extractField(/overallResult=(\w+)/);
      const tamperResult = extractField(/tamperResult=(\w+)/);

      const idScanResult = parseResultDetail("idScanResult");
      const idScanVerificationResult = parseResultDetail(
        "idScanVerificationResult"
      );
      const idScanSummaryResult = parseResultDetail("idScanSummaryResult");

      const parseImageClassificationDetail = (fieldName) => {
        const match = notesStr.match(
          new RegExp(`${fieldName}=ImageClassificationDetail\\(([\\s\\S]*?)\\)`)
        );
        if (!match) return null;

        const detailStr = match[1];
        const nameMatch = detailStr.match(/name=([^,]+)/);
        const classNameMatch = detailStr.match(/className=([^,]+)/);
        const countryCodeMatch = detailStr.match(/countryCode=([^,]+)/);
        const issueYearMatch = detailStr.match(/issueYear=([^,]+)/);

        const detail = {
          name: normalizeValue(nameMatch?.[1]),
          className: normalizeValue(classNameMatch?.[1]),
          countryCode: normalizeValue(countryCodeMatch?.[1]),
          issueYear: normalizeValue(issueYearMatch?.[1]),
        };

        return Object.values(detail).some(Boolean) ? detail : null;
      };

      const imageClassification = {
        front: parseImageClassificationDetail("frontImageClassification"),
        back: parseImageClassificationDetail("backImageClassification"),
        overall: parseImageClassificationDetail("overallImageClassification"),
      };

      const locatedIdScanRecord = parseLocatedIdScanRecord();

      // Extract located ID info
      const firstName =
        locatedIdScanRecord?.firstName ||
        extractField(/idScanFirstName=([^,)]+)/);
      const middleName =
        locatedIdScanRecord?.middleName ||
        extractField(/idScanMiddleName=([^,)]+)/);
      const lastName =
        locatedIdScanRecord?.lastName ||
        extractField(/idScanLastName=([^,)]+)/);
      // Extract DOB with both year and month
      const dateOfBirthMatch = notesStr.match(
        /idScanDateOfBirth=IdScanDate\(year=(\d+), month=(\d+)\)/
      );
      const dateOfBirth = dateOfBirthMatch
        ? `${dateOfBirthMatch[2].padStart(2, "0")}/${dateOfBirthMatch[1]}`
        : locatedIdScanRecord?.dateOfBirth || null;
      const expirationDateMatch = notesStr.match(
        /idScanExpirationDate=IdScanDate\(year=(\d+), month=(\d+)\)/
      );
      const expirationDate = expirationDateMatch
        ? `${expirationDateMatch[2].padStart(2, "0")}/${expirationDateMatch[1]}`
        : locatedIdScanRecord?.expirationDate || null;
      const dateOfIssuanceMatch = notesStr.match(
        /idScanDateOfIssuance=IdScanDate\(year=(\d+), month=(\d+)\)/
      );
      const dateOfIssuance = dateOfIssuanceMatch
        ? `${dateOfIssuanceMatch[2].padStart(2, "0")}/${dateOfIssuanceMatch[1]}`
        : locatedIdScanRecord?.dateOfIssuance || null;
      // Extract address components separately
      const streetNumber =
        locatedIdScanRecord?.streetNumber ||
        extractField(/idScanStreetNumber=([\d.]+)/);
      const streetName =
        locatedIdScanRecord?.streetName ||
        extractField(/idScanStreetName=([^,]+)/);
      const city =
        locatedIdScanRecord?.city || extractField(/idScanCity=([^,]+)/);
      const state =
        locatedIdScanRecord?.state || extractField(/idScanState=([^,)]+)/);
      const zip =
        locatedIdScanRecord?.zip || extractField(/idScanZip=([^,)]+)/);
      const docType =
        locatedIdScanRecord?.documentType ||
        extractField(/idScanDocumentType=(\w+)/);
      const docNumber =
        locatedIdScanRecord?.documentNumber ||
        extractField(/idScanDocumentNumber=([^,)]+)/);
      const confidence =
        locatedIdScanRecord?.confidenceScore ||
        extractField(/idScanConfidenceScore=([\d.]+)/);

      // Extract image quality
      const frontSharpness = extractField(
        /frontImageDetails=.*?sharpness=(\d+)/
      );
      const frontGlare = extractField(/frontImageDetails=.*?glare=(\d+)/);
      const backSharpness = extractField(/backImageDetails=.*?sharpness=(\d+)/);
      const backGlare = extractField(/backImageDetails=.*?glare=(\d+)/);

      // Extract authentication results - use [\s\S]*? to handle nested parentheses in text
      const authMatches =
        notesStr.match(/AuthenticationResult\([\s\S]*?model=null\)/g) || [];
      const authResults = authMatches
        .map((m) => {
          const statusMatch = m.match(/status=([^,]+)/);
          const nameMatch = m.match(/name=([^,]+)/);
          const dispositionMatch = m.match(/disposition=([^,]+)/);
          // Capture documentRegion value
          const regionMatch = m.match(/documentRegion=([^,)]+)/);
          // Capture actions - match from "actions=" to before "documentRegion="
          const actionsMatch = m.match(
            /actions=([^,]+(?:,[^,=]*)*?)(?=,\s*documentRegion)/
          );

          let documentRegion = null;
          if (regionMatch) {
            documentRegion = normalizeValue(regionMatch[1]);
          }

          const actions = normalizeValue(actionsMatch?.[1]);

          const result =
            statusMatch && nameMatch && dispositionMatch
              ? {
                  status: normalizeValue(statusMatch[1]),
                  name: normalizeValue(nameMatch[1]),
                  disposition: normalizeValue(dispositionMatch[1]),
                  actions,
                  documentRegion,
                }
              : null;
          return result;
        })
        .filter(
          (item) =>
            item &&
            (item.status ||
              item.name ||
              item.disposition ||
              item.actions ||
              item.documentRegion)
        );

      // Extract qualifiers
      const qualifiersMatch = notesStr.match(
        /qualifiers=Qualifiers\(qualifier=\[([^\]]+)\]\)/
      );
      const qualifiersStr = qualifiersMatch?.[1];
      const qualifiers = [];

      if (qualifiersStr) {
        // Match each Qualifier object
        const qualifierMatches =
          qualifiersStr.match(/Qualifier\(([^)]+)\)/g) || [];
        qualifierMatches.forEach((q) => {
          const keyMatch = q.match(/key=([^,]+)/);
          const messageMatch = q.match(/message=([^,]+)/);
          const key = normalizeValue(keyMatch?.[1]);
          const message = normalizeValue(messageMatch?.[1]);
          if (key || message) {
            qualifiers.push({
              key,
              message,
            });
          }
        });
      }

      return {
        overallResult,
        tamperResult,
        idScanResult,
        idScanVerificationResult,
        idScanSummaryResult,
        imageClassification,
        firstName,
        middleName,
        lastName,
        dateOfBirth,
        expirationDate,
        dateOfIssuance,
        locatedIdScanRecord,
        address: {
          streetNumber,
          streetName,
          city,
          state,
          zip,
        },
        docType,
        docNumber,
        confidence,
        imageQuality: {
          front: { sharpness: frontSharpness, glare: frontGlare },
          back: { sharpness: backSharpness, glare: backGlare },
        },
        qualifiers: dedupeQualifiers(qualifiers),
        authResults,
      };
    } catch (err) {
      return null;
    }
  };

  // Parse SCAN_PENDING_FAIL notes field
  const parseScanPendingFail = (notesStr) => {
    try {
      const extractReasonToken = (text, token) => {
        if (!text) return null;
        const regex = new RegExp(`(?:^|;\\s*)${token}=([^;]+)`, "i");
        const match = text.match(regex);
        return match?.[1]?.trim() || null;
      };

      const splitMessageAndKey = (value) => {
        if (!value) return { message: null, key: null };
        const keyMatch = value.match(/^(.*?)(?:\s*\(([^)]+)\))?$/);
        return {
          message: keyMatch?.[1]?.trim() || null,
          key: keyMatch?.[2]?.trim() || null,
        };
      };

      // Extract ResultDetail
      const resultMatch = notesStr.match(
        /ResultDetail\(key=([^,]+),\s*message=([^)]+)\)/
      );
      const resultKey = resultMatch?.[1]?.trim();
      const resultMessage = resultMatch?.[2]?.trim();

      // Fallback for semicolon-delimited reason format
      const summaryToken = extractReasonToken(notesStr, "summary");
      const resultToken = extractReasonToken(notesStr, "result");
      const idliveqToken = extractReasonToken(notesStr, "idliveqError");
      const restrictionToken = extractReasonToken(notesStr, "restriction");
      const qualifiersToken = extractReasonToken(notesStr, "qualifiers");
      const summaryParsed = splitMessageAndKey(summaryToken);
      const resultParsed = splitMessageAndKey(resultToken);
      const idliveqParsed = splitMessageAndKey(idliveqToken);
      const restrictionParsed = splitMessageAndKey(restrictionToken);

      // Extract Qualifiers
      const qualifiersMatch = notesStr.match(
        /Qualifiers\(qualifier=\[([^\]]+)\]\)/
      );
      const qualifiersStr = qualifiersMatch?.[1];
      const qualifiers = [];

      if (qualifiersStr) {
        // Match each Qualifier object
        const qualifierMatches =
          qualifiersStr.match(/Qualifier\(([^)]+)\)/g) || [];
        qualifierMatches.forEach((q) => {
          const keyMatch = q.match(/key=([^,]+)/);
          const messageMatch = q.match(/message=([^,]+)/);
          qualifiers.push({
            key: keyMatch?.[1]?.trim(),
            message: messageMatch?.[1]?.trim(),
          });
        });
      }

      if (qualifiers.length === 0 && qualifiersToken) {
        qualifiersToken
          .split("|")
          .map((entry) => splitMessageAndKey(entry.trim()))
          .filter((entry) => entry.message || entry.key)
          .forEach((entry) => qualifiers.push(entry));
      }

      // Extract idKYC
      const idKycMatch = notesStr.match(/idKYC:\s*(\d+)/i);
      const idKyc = idKycMatch?.[1];

      return {
        resultKey: resultKey || summaryParsed.key || resultParsed.key,
        resultMessage:
          resultMessage || summaryParsed.message || resultParsed.message,
        summary: {
          key: summaryParsed.key,
          message: summaryParsed.message,
        },
        results: {
          key: resultParsed.key,
          message: resultParsed.message,
        },
        idliveqError: {
          key: idliveqParsed.key,
          message: idliveqParsed.message,
        },
        restriction: {
          key: restrictionParsed.key,
          message: restrictionParsed.message,
          matches: [],
        },
        qualifiers: dedupeQualifiers(qualifiers),
        idKyc,
        reason: notesStr,
      };
    } catch (err) {
      return null;
    }
  };

  // Parse SCAN_PENDING_PASS notes field
  const parseScanPendingPass = (notesStr) => {
    try {
      // Extract ResultDetail
      const resultMatch = notesStr.match(
        /ResultDetail\(key=([^,]+),\s*message=([^)]+)\)/
      );
      const resultKey = resultMatch?.[1]?.trim();
      const resultMessage = resultMatch?.[2]?.trim();

      // Extract idKYC
      const idKycMatch = notesStr.match(/idKYC:\s*(\d+)/);
      const idKyc = idKycMatch?.[1];

      return {
        resultKey,
        resultMessage,
        summary: {
          key: resultKey,
          message: resultMessage,
        },
        results: {
          key: null,
          message: null,
        },
        idliveqError: {
          key: null,
          message: null,
        },
        restriction: {
          key: null,
          message: null,
          matches: [],
        },
        qualifiers: [],
        idKyc,
        idScan: null,
        source: null,
        reason: notesStr,
      };
    } catch (err) {
      return null;
    }
  };

  // Parse FAILED KYC check notes field
  const parseFailedKycCheck = (notesStr) => {
    try {
      // Extract error message (e.g., "GBG verification failed")
      const errorMatch = notesStr.match(/Automated KYC check:\s*([^:]+):/);
      const errorMessage = errorMatch?.[1]?.trim();

      // Extract idKYC
      const idKycMatch = notesStr.match(/idKYC:\s*(\d+)/);
      const idKyc = idKycMatch?.[1];

      // Extract summaryResult message
      const summaryMatch = notesStr.match(
        /summaryResult=ResultDetail\(key=([^,]+),\s*message=([^)]+)\)/
      );
      const summaryMessage = summaryMatch?.[2]?.trim();

      // Extract results message
      const resultsMatch = notesStr.match(
        /results=ResultDetail\(key=([^,]+),\s*message=([^)]+)\)/
      );
      const resultsMessage = resultsMatch?.[2]?.trim();

      // Extract idLiveQError message if it exists
      const idLiveQMatch = notesStr.match(
        /idLiveQError=ResultDetail\(key=([^,]+),\s*message=([^)]+)\)/
      );
      const idLiveQMessage = idLiveQMatch?.[2]?.trim();

      return {
        errorMessage,
        idKyc,
        summaryMessage,
        resultsMessage,
        idLiveQMessage,
      };
    } catch (err) {
      return null;
    }
  };

  // Parse Automatic KYC Result notes field
  const parseAutomaticKycResult = (notesStr) => {
    try {
      // Extract error message (e.g., "KYC verification failed")
      const errorMatch = notesStr.match(/Automated KYC check:\s*([^:]+):/);
      const errorMessage = errorMatch?.[1]?.trim();

      // Extract idKYC
      const idKycMatch = notesStr.match(/idKYC:\s*(\d+)/);
      const idKyc = idKycMatch?.[1];

      // Extract summaryResult message (after Response object)
      const summaryMatch = notesStr.match(
        /summaryResult=ResultDetail\(key=([^,]+),\s*message=([^)]+)\)/
      );
      const summaryMessage = summaryMatch?.[2]?.trim();

      // Extract results message
      const resultsMatch = notesStr.match(
        /results=ResultDetail\(key=([^,]+),\s*message=([^)]+)\)/
      );
      const resultsMessage = resultsMatch?.[2]?.trim();

      // Extract idLiveQError message if it exists
      const idLiveQMatch = notesStr.match(
        /idLiveQError=ResultDetail\(key=([^,]+),\s*message=([^)]+)\)/
      );
      const idLiveQMessage = idLiveQMatch?.[2]?.trim();

      return {
        errorMessage,
        idKyc,
        summaryMessage,
        resultsMessage,
        idLiveQMessage,
      };
    } catch (err) {
      return null;
    }
  };

  const parseStructuredScanResult = (entry) => {
    const details = entry?.details;
    if (!details || typeof details !== "object") return null;
    if (!details.response || typeof details.response !== "object") return null;

    const response = details.response || {};
    const located = response.locatedIdScanRecord || {};
    const additional = response.additionalScanDetails || {};
    const imageClassification = additional.imageClassification || {};
    const imageDetails = additional.imageDetails || {};
    const qualifiersSource =
      response.qualifiers?.qualifier || response.qualifiers;
    const qualifiers = Array.isArray(qualifiersSource) ? qualifiersSource : [];

    const authSource =
      additional.authenticationResults?.authenticationResult ||
      additional.authenticationResult ||
      [];
    const authResults = Array.isArray(authSource)
      ? authSource.map((a) => ({
          status: a?.status || null,
          name: a?.name || null,
          disposition: a?.disposition || null,
          actions: a?.actions || null,
          documentRegion: a?.documentRegion || null,
        }))
      : [];

    const formatDate = (idScanDate) => {
      if (!idScanDate || !idScanDate.year || !idScanDate.month) return null;
      return `${String(idScanDate.month).padStart(2, "0")}/${idScanDate.year}`;
    };

    const confidenceValue =
      located.idScanConfidenceScore != null
        ? String(located.idScanConfidenceScore)
        : details.score || null;

    return {
      overallResult: additional.overallResult || response.overallResult || null,
      tamperResult: additional.tamperResult || response.tamperResult || null,
      idScanResult: response.idScanResult || null,
      idScanVerificationResult: response.idScanVerificationResult || null,
      idScanSummaryResult: response.idScanSummaryResult || null,
      imageClassification: {
        front: imageClassification.frontImageClassification || null,
        back: imageClassification.backImageClassification || null,
        overall: imageClassification.overallImageClassification || null,
      },

      firstName: located.idScanFirstName || null,
      middleName: located.idScanMiddleName || null,
      lastName: located.idScanLastName || null,
      dateOfBirth: formatDate(located.idScanDateOfBirth),
      expirationDate: formatDate(located.idScanExpirationDate),
      dateOfIssuance: formatDate(located.idScanDateOfIssuance),
      locatedIdScanRecord: {
        firstName: located.idScanFirstName || null,
        middleName: located.idScanMiddleName || null,
        lastName: located.idScanLastName || null,
        streetNumber: located.idScanStreetNumber || null,
        streetName: located.idScanStreetName || null,
        city: located.idScanCity || null,
        state: located.idScanState || null,
        zip: located.idScanZip || null,
        documentType: located.idScanDocumentType || null,
        documentNumber: located.idScanDocumentNumber || null,
        confidenceScore: confidenceValue,
        dateOfBirth: formatDate(located.idScanDateOfBirth),
        dateOfIssuance: formatDate(located.idScanDateOfIssuance),
        expirationDate: formatDate(located.idScanExpirationDate),
      },
      address: {
        streetNumber: located.idScanStreetNumber || null,
        streetName: located.idScanStreetName || null,
        city: located.idScanCity || null,
        state: located.idScanState || null,
        zip: located.idScanZip || null,
      },
      docType: located.idScanDocumentType || null,
      docNumber: located.idScanDocumentNumber || null,
      confidence: confidenceValue,
      imageQuality: {
        front: {
          sharpness: imageDetails.frontImageDetails?.sharpness || null,
          glare: imageDetails.frontImageDetails?.glare || null,
        },
        back: {
          sharpness: imageDetails.backImageDetails?.sharpness || null,
          glare: imageDetails.backImageDetails?.glare || null,
        },
      },
      qualifiers: dedupeQualifiers(
        qualifiers
          .map((q) => ({ key: q?.key || null, message: q?.message || null }))
          .filter((q) => q.key || q.message)
      ),
      authResults: authResults.filter(
        (a) =>
          a.status || a.name || a.disposition || a.actions || a.documentRegion
      ),
    };
  };

  const formatExternalCreationDate = (dateValue) => {
    if (!dateValue) return "-";
    const dateText = String(dateValue);
    if (/^\d{4}-\d{2}-\d{2}/.test(dateText)) {
      return dateText.slice(0, 10);
    }
    const parsedDate = new Date(dateText);
    return Number.isNaN(parsedDate.getTime())
      ? dateText
      : parsedDate.toISOString().slice(0, 10);
  };

  const parseStructuredScanPending = (entry) => {
    let details = entry?.details;
    if ((!details || typeof details !== "object") && entry?.notes) {
      try {
        const parsedNotes = JSON.parse(entry.notes);
        if (parsedNotes && typeof parsedNotes === "object") {
          details = parsedNotes;
        }
      } catch (e) {
        // Legacy non-JSON notes should continue to the text parser fallback.
      }
    }
    if (!details || typeof details !== "object") return null;
    if (details.source === "LEGACY_NOTES") return null;
    const response =
      details.response && typeof details.response === "object"
        ? details.response
        : details;
    if (!response || typeof response !== "object") return null;

    const getField = (obj, ...keys) => {
      if (!obj || typeof obj !== "object") return null;
      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          return obj[key];
        }
      }
      return null;
    };

    const summary = getField(response, "summaryResult", "summary-result") || {};
    const results = getField(response, "results") || {};
    const idliveqError =
      getField(response, "idliveqError", "idLiveQError", "idliveq-error") || {};
    const restriction = getField(response, "restriction") || {};

    const qualifiersContainer = getField(response, "qualifiers") || {};
    const qualifiersSource =
      getField(qualifiersContainer, "qualifier") || qualifiersContainer;
    const qualifiers = Array.isArray(qualifiersSource)
      ? qualifiersSource
      : qualifiersSource && typeof qualifiersSource === "object"
        ? [qualifiersSource]
        : [];

    const restrictionMatches = Array.isArray(restriction.pa)
      ? restriction.pa
      : restriction.pa && typeof restriction.pa === "object"
        ? [restriction.pa]
        : [];

    const extractReasonToken = (text, token) => {
      if (!text) return null;
      const regex = new RegExp(`(?:^|;\\s*)${token}=([^;]+)`, "i");
      const match = text.match(regex);
      return match?.[1]?.trim() || null;
    };

    const splitMessageAndKey = (value) => {
      if (!value) return { message: null, key: null };
      const keyMatch = value.match(/^(.*?)(?:\s*\(([^)]+)\))?$/);
      return {
        message: keyMatch?.[1]?.trim() || null,
        key: keyMatch?.[2]?.trim() || null,
      };
    };

    const summaryFromReason = splitMessageAndKey(
      extractReasonToken(details.reason || entry?.notes, "summary")
    );
    const resultsFromReason = splitMessageAndKey(
      extractReasonToken(details.reason || entry?.notes, "result")
    );
    const idliveqFromReason = splitMessageAndKey(
      extractReasonToken(details.reason || entry?.notes, "idliveqError")
    );
    const restrictionFromReason = splitMessageAndKey(
      extractReasonToken(details.reason || entry?.notes, "restriction")
    );
    const qualifiersFromReason = extractReasonToken(
      details.reason || entry?.notes,
      "qualifiers"
    );

    const fallbackQualifiers = qualifiersFromReason
      ? qualifiersFromReason
          .split("|")
          .map((item) => splitMessageAndKey(item.trim()))
          .filter((item) => item.key || item.message)
      : [];

    return {
      resultKey: summary.key || details.status || entry?.action || null,
      resultMessage: summary.message || details.status || entry?.action || null,
      summary: {
        key: summary.key || summaryFromReason.key || null,
        message: summary.message || summaryFromReason.message || null,
      },
      results: {
        key: results.key || resultsFromReason.key || null,
        message: results.message || resultsFromReason.message || null,
      },
      idliveqError: {
        key: idliveqError.key || idliveqFromReason.key || null,
        message: idliveqError.message || idliveqFromReason.message || null,
      },
      restriction: {
        key: restriction.key || restrictionFromReason.key || null,
        message: restriction.message || restrictionFromReason.message || null,
        matches: restrictionMatches.map((m) => ({
          list: m?.list || null,
          score: m?.score ?? null,
          dob: m?.dob || null,
        })),
      },
      qualifiers: dedupeQualifiers([
        ...qualifiers
          .map((q) => ({ key: q?.key || null, message: q?.message || null }))
          .filter((q) => q.key || q.message),
        ...fallbackQualifiers,
      ]),
      idKyc:
        details.idKyc ||
        details.idKYC ||
        getField(response, "idNumber", "id-number") ||
        entry?.notes?.match(/idKYC:\s*(\d+)/i)?.[1] ||
        null ||
        null,
      source: details.source || null,
      status: details.status || entry?.action || null,
      idScan: getField(response, "idScan", "id-scan"),
      scanLinkProvided: details.scanLinkProvided,
      reason: details.reason || null,
    };
  };

  const formatRestrictionDob = (dob) => {
    if (!dob) return null;
    const cleaned = String(dob).replace(/\D/g, "");
    if (cleaned.length === 8) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;
    }
    return dob;
  };

  async function fetchSecureInfo() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL +
        `/member/secureinfo?memberId=` +
        memberInfo.memberId +
        `&companyId=` +
        memberInfo.companyId
    );
    return response;
  }

  const {
    data: dataSecureInfo,
    isError: isErrorSecureInfo,
    isFetching: isFetchingSecureInfo,
    isLoading: isLoadingSecureInfo,
  } = useQuery(["secureInfo"], fetchSecureInfo, {
    onError: (error) => {
      setFeedback({
        open: true,
        type: "error",
        message: error.message || "Error fetching secure information",
      });
    },
  });

  useEffect(() => {
    const { data } = dataSecureInfo || {};
    setSecureInfoData(data || {});
  }, [dataSecureInfo]);

  // Mutation for updating date of birth
  const updateDateOfBirthMutation = useMutation(
    (request) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/member/updateDateOfBirth`,
        request
      );
    },
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "memberSecureInfo.dobUpdated",
            "Date of birth updated successfully"
          ),
        });
        setIsEditingDOB(false);
        queryClient.invalidateQueries(["secureInfo"]);
      },
      onError: (err) => {
        console.log(err);
        const msg = err?.response?.data?.description || err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  // Mutation for updating identification
  const updateIdentificationMutation = useMutation(
    (request) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/member/updateIdentification`,
        request
      );
    },
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "memberSecureInfo.identificationUpdated",
            "Identification updated successfully"
          ),
        });
        setIsEditingIdentification(false);
        queryClient.invalidateQueries(["secureInfo"]);
      },
      onError: (err) => {
        console.log(err);
        const msg = err?.response?.data?.description || err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  // KYC review dialog state
  const [kycReviewOpen, setKycReviewOpen] = useState(false);
  const [kycUploadDialogOpen, setKycUploadDialogOpen] = useState(false);

  // KYC Request Info States
  const [requestInfoMode, setRequestInfoMode] = useState(false);
  const [requestNotes, setRequestNotes] = useState("");
  const [requestedDocs, setRequestedDocs] = useState([]);
  const [requestExtraEvidence, setRequestExtraEvidence] = useState("");
  const [approveNotes, setApproveNotes] = useState("");
  const [approveTier, setApproveTier] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");
  const [reviewUploadFile, setReviewUploadFile] = useState(null);
  const [reviewUploadInputKey, setReviewUploadInputKey] = useState(0);
  const [reviewUploadDocType, setReviewUploadDocType] = useState("PASSPORT");
  const [reviewUploadSide, setReviewUploadSide] = useState("FRONT");
  const [reviewUploadCountryCode, setReviewUploadCountryCode] = useState("");
  const [uploadedReviewDocs, setUploadedReviewDocs] = useState([]);
  const ALLOWABLE_DOCUMENTS = [
    { value: "ITIN", label: "ITIN Letter" },
    { value: "COUNTRY_ID", label: "Country Identification" },
    { value: "PASSPORT", label: "Passport" },
    { value: "DRIVER_LICENSE", label: "Driver's License" },
    { value: "STATE_ID", label: "State Identification" },
    { value: "TRIBAL_ID", label: "Tribal Identification" },
    { value: "MILITARY_ID", label: "Military Identification" },
    { value: "VETERAN_ID", label: "Veteran Identification" },
    { value: "MATRICULA_CONSULAR", label: "Matricula Consular" },
    { value: "STATEMENT", label: "Bank Statement" },
    { value: "UTILITY_BILL", label: "Utility bill" },
    { value: "PAYSTUB", label: "Paystub" },
    { value: "OTHER", label: "Other" },
  ];

  const ALLOWABLE_DOCUMENT_SIDES = ["FRONT", "BACK", "SINGLE"];

  const kycReviewMutation = useMutation(
    (request) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/member/kyc-review`,
        request
      );
    },
    {
      onSuccess: (_, request) => {
        setKycReviewOpen(false);
        setRequestInfoMode(false);
        setRequestNotes("");
        setRequestedDocs([]);
        setRequestExtraEvidence("");
        setApproveNotes("");
        setApproveTier("");
        setRejectNotes("");
        setReviewUploadFile(null);
        setReviewUploadInputKey((prev) => prev + 1);
        setReviewUploadDocType("PASSPORT");
        setReviewUploadSide("FRONT");
        setReviewUploadCountryCode("");
        setUploadedReviewDocs([]);

        let successMessage = "";
        if (request.decision === "APPROVED") {
          successMessage = Labels.getLabel(
            "memberSecureInfo.kycApproved",
            "KYC overridden to PASSED — workflow will resume"
          );
        } else if (request.decision === "REQUEST_INFO") {
          successMessage = Labels.getLabel(
            "memberSecureInfo.kycRequestInfoSuccess",
            "Additional information requested successfully — waiting for customer."
          );
        } else {
          successMessage = Labels.getLabel(
            "memberSecureInfo.kycRejected",
            "KYC confirmed as REJECTED — workflow will be finalized"
          );
        }

        setFeedback({
          open: true,
          type: "success",
          message: successMessage,
        });
        queryClient.invalidateQueries(["secureInfo"]);
      },
      onError: (err) => {
        const msg = err?.response?.data || err.message;
        setFeedback({ open: true, type: "error", message: msg });
      },
    }
  );

  const kycRepairMutation = useMutation(
    () =>
      HttpService.getAxiosClient().post(window.API_URL + `/member/kyc-repair`, {
        customerId: secureInfoData?.customerId || memberInfo?.customerId,
      }),
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message:
            "KYC reprocessing started. Replacement documents can now be uploaded.",
        });
        queryClient.invalidateQueries(["secureInfo"]);
      },
      onError: (err) => {
        const msg = err?.response?.data || err.message;
        setFeedback({ open: true, type: "error", message: msg });
      },
    }
  );

  const kycDocumentUploadMutation = useMutation(
    (payload) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/member/kyc-documents/upload`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    },
    {
      onSuccess: (res) => {
        if (res?.data) {
          setUploadedReviewDocs((prev) => [...prev, res.data]);
        }
        setReviewUploadFile(null);
        setReviewUploadInputKey((prev) => prev + 1);
        setFeedback({
          open: true,
          type: "success",
          message: "Document uploaded and attached to this review.",
        });
        queryClient.invalidateQueries(["kycDocuments"]);
      },
      onError: (err) => {
        const msg = err?.response?.data || err.message;
        setFeedback({ open: true, type: "error", message: msg });
      },
    }
  );

  const parseManualEvidence = (raw) => {
    if (!raw) return [];
    return raw
      .split(/\r?\n|,|;/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const buildRequestedDocumentsPayload = (
    selectedDocs = [],
    rawEvidence = ""
  ) => {
    const manualDocs = parseManualEvidence(rawEvidence);
    return Array.from(new Set([...(selectedDocs || []), ...manualDocs]));
  };

  const handleUploadReviewDocument = () => {
    if (!reviewUploadFile) {
      setFeedback({
        open: true,
        type: "warning",
        message: "Please choose a file to upload.",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64String = String(event.target.result).split(",")[1];
      const payload = {
        fileName: reviewUploadFile.name,
        contentType: reviewUploadFile.type || "application/octet-stream",
        size: reviewUploadFile.size,
        base64Content: base64String,
        memberId: memberInfo?.memberId || secureInfoData?.memberId || "",
        companyId: memberInfo?.companyId || "",
        documentType: reviewUploadDocType,
        side: reviewUploadSide,
        countryCode: reviewUploadCountryCode || "",
      };

      kycDocumentUploadMutation.mutate(payload);
    };
    reader.onerror = () => {
      setFeedback({
        open: true,
        type: "error",
        message: "Unable to read the selected file.",
      });
    };
    reader.readAsDataURL(reviewUploadFile);
  };

  const removeUploadedReviewDoc = (idxToRemove) => {
    setUploadedReviewDocs((prev) =>
      prev.filter((_, index) => index !== idxToRemove)
    );
  };

  // Check if editing is allowed based on dataSecureInfo properties and roles
  const canEditDOB =
    secureInfoData?.canEditDOB === "true" &&
    UserService.hasRole(["member-change-dob"]);
  const canEditIdentification =
    secureInfoData?.canEditIdentification === "true" &&
    UserService.hasRole(["member-change-identification"]);

  // Handle edit click for DOB
  const handleEditDOB = () => {
    setTempDateOfBirth(secureInfoData?.dateOfBirth || "");
    setIsEditingDOB(true);
  };

  // Handle save DOB
  const handleSaveDOB = () => {
    // Validate format yyyy-MM-dd
    const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;

    if (!tempDateOfBirth) {
      setFeedback({
        open: true,
        type: "warning",
        message: Labels.getLabel(
          "memberSecureInfo.dobRequired",
          "Date of birth is required"
        ),
      });
      return;
    }

    // Check length (yyyy-MM-dd should be exactly 10 characters)
    if (tempDateOfBirth.length !== 10) {
      setFeedback({
        open: true,
        type: "warning",
        message: Labels.getLabel(
          "memberSecureInfo.dobInvalidLength",
          "Date of birth must be exactly 10 characters (yyyy-MM-dd)"
        ),
      });
      return;
    }

    if (!dateFormatRegex.test(tempDateOfBirth)) {
      setFeedback({
        open: true,
        type: "warning",
        message: Labels.getLabel(
          "memberSecureInfo.dobInvalidFormat",
          "Date of birth must be in format yyyy-MM-dd (e.g., 1990-12-31)"
        ),
      });
      return;
    }

    // Validate that it's a valid date
    const [year, month, day] = tempDateOfBirth.split("-").map(Number);
    const date = new Date(year, month - 1, day);

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      setFeedback({
        open: true,
        type: "warning",
        message: Labels.getLabel(
          "memberSecureInfo.dobInvalidDate",
          "Please enter a valid date"
        ),
      });
      return;
    }

    if (tempDateOfBirth === secureInfoData?.dateOfBirth) {
      setFeedback({
        open: true,
        type: "warning",
        message: Labels.getLabel(
          "memberSecureInfo.noChangeDOB",
          "Date of birth must be different from the current value"
        ),
      });
      return;
    }

    updateDateOfBirthMutation.mutate({
      customerId: memberInfo.idCustomer,
      dateOfBirth: tempDateOfBirth,
    });
  };

  // Handle cancel DOB
  const handleCancelDOB = () => {
    setIsEditingDOB(false);
    setTempDateOfBirth("");
  };

  // Handle edit click for Identification
  const handleEditIdentification = () => {
    setTempIdentification(secureInfoData?.identification || "");
    setIsEditingIdentification(true);
  };

  // Handle save Identification
  const handleSaveIdentification = () => {
    if (!tempIdentification) {
      setFeedback({
        open: true,
        type: "warning",
        message: Labels.getLabel(
          "memberSecureInfo.identificationRequired",
          "Identification is required"
        ),
      });
      return;
    }

    // Validate based on identificationType
    const identificationType = secureInfoData?.identificationType;

    if (identificationType === "1") {
      // SSN validation
      // Check length: SSN should be 9 digits (without dashes) or 11 with dashes
      const cleanedSSN = tempIdentification.replace(/-/g, "");
      if (cleanedSSN.length !== 9) {
        setFeedback({
          open: true,
          type: "warning",
          message: Labels.getLabel(
            "memberSecureInfo.ssnInvalidLength",
            "SSN must be 9 digits (with or without dashes)"
          ),
        });
        return;
      }

      const ssnRegex =
        /^(?!219099999|078051120)(?!666|000|9\d{2})\d{3}-?\d{2}-?\d{4}$/;
      if (!ssnRegex.test(tempIdentification)) {
        setFeedback({
          open: true,
          type: "warning",
          message: Labels.getLabel(
            "memberSecureInfo.invalidSSN",
            "Invalid SSN"
          ),
        });
        return;
      }
    } else if (identificationType === "8") {
      // ITIN validation
      // Check length: ITIN should be exactly 9 digits
      if (tempIdentification.length !== 9) {
        setFeedback({
          open: true,
          type: "warning",
          message: Labels.getLabel(
            "memberSecureInfo.itinInvalidLength",
            "ITIN must be exactly 9 digits"
          ),
        });
        return;
      }

      const itinRegex = /^9\d{8}$/;
      if (!itinRegex.test(tempIdentification)) {
        setFeedback({
          open: true,
          type: "warning",
          message: Labels.getLabel(
            "memberSecureInfo.invalidITIN",
            "Invalid ITIN"
          ),
        });
        return;
      }
    } else {
      // Other identification types - basic validation
      // Allows digits and hyphens, minimum 9 maximum 15 characters
      const otherIdRegex = /^[\d-]{9,15}$/;
      if (!otherIdRegex.test(tempIdentification)) {
        setFeedback({
          open: true,
          type: "warning",
          message: Labels.getLabel(
            "memberSecureInfo.invalidIdentification",
            "Identification must be 9-15 characters with digits or hyphens"
          ),
        });
        return;
      }
    }

    if (tempIdentification === secureInfoData?.identification) {
      setFeedback({
        open: true,
        type: "warning",
        message: Labels.getLabel(
          "memberSecureInfo.noChangeIdentification",
          "Identification must be different from the current value"
        ),
      });
      return;
    }

    // Remove dashes before sending to backend
    const cleanedIdentification = tempIdentification.replace(/-/g, "");

    updateIdentificationMutation.mutate({
      customerId: memberInfo.idCustomer,
      identificationType: secureInfoData?.identificationType || "",
      identificationValue: cleanedIdentification,
    });
  };

  // Handle cancel Identification
  const handleCancelIdentification = () => {
    setIsEditingIdentification(false);
    setTempIdentification("");
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return; //avoid closing the modal by clicking the backdrop
    props.setOpen(false);
  };

  return (
    <>
      <Dialog
        maxWidth={tabValue === 1 ? "xl" : "lg"}
        fullWidth
        open={props.open}
        onClose={props.handleClose}
        disableEscapeKeyDown={true}
      >
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={(e) => e.preventDefault()}
        >
          {/* Title bar with same style as MemberDetail */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "primary.main",
              px: 3,
              minHeight: 52,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" component="div" color="white">
                {props.dialogTitle}
              </Typography>
              {isFetchingSecureInfo ? (
                <CircularProgress sx={{ color: "white" }} size={20} />
              ) : (
                <SecurityIcon sx={{ color: "white" }} />
              )}
            </Box>
            <Box>
              <IconButton
                edge="end"
                color="inherit"
                size="small"
                onClick={handleClose}
                aria-label="close"
              >
                <CloseIcon sx={{ color: "white" }} />
              </IconButton>
            </Box>
          </Box>

          <DialogContent
            sx={{ width: "95%", flexShrink: 0, paddingTop: 1, minHeight: 500 }}
          >
            {(isLoadingSecureInfo ||
              (!isErrorSecureInfo && secureInfoData)) && (
              <Box
                sx={{
                  mb: 2,
                  px: 1,
                  visibility: isLoadingSecureInfo ? "hidden" : "visible",
                }}
              >
                <Typography variant="h5" color="primary" fontWeight="bold">
                  {secureInfoData?.firstName} {secureInfoData?.lastName}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 1 }}
                >
                  <Chip
                    label={`Member ID: ${memberInfo?.memberId || secureInfoData?.memberId || "N/A"}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      bgcolor: "grey.50",
                    }}
                  />
                  <Chip
                    label={`Customer ID: ${
                      secureInfoData?.customerId ||
                      memberInfo?.idCustomer ||
                      memberInfo?.customerId ||
                      "N/A"
                    }`}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      bgcolor: "grey.50",
                    }}
                  />
                  {secureInfoData?.cardProxy && (
                    <Chip
                      label={`Card Proxy: ${secureInfoData.cardProxy}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: "bold",
                        bgcolor: "grey.50",
                      }}
                    />
                  )}
                  {secureInfoData?.cardStatus && (
                    <Chip
                      label={`Card Status: ${secureInfoData.cardStatus}`}
                      size="small"
                      color={
                        [
                          "ASSIGNED",
                          "ACTIVE",
                          "ISSUED",
                          "Card Issued",
                        ].includes(secureInfoData.cardStatus)
                          ? "success"
                          : "default"
                      }
                      variant="outlined"
                      sx={{ fontWeight: "bold" }}
                    />
                  )}
                  {secureInfoData?.kycTier && (
                    <Chip
                      label={`Tier: ${String(secureInfoData.kycTier).replace(
                        /^TIER_/i,
                        ""
                      )}`}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ fontWeight: "bold" }}
                    />
                  )}
                </Stack>
              </Box>
            )}

            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            >
              <Tab
                icon={<PersonIcon sx={{ fontSize: "1.2rem" }} />}
                iconPosition="start"
                label="Member Profile"
              />
              <Tab
                icon={<SecurityIcon sx={{ fontSize: "1.2rem" }} />}
                iconPosition="start"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    KYC Evaluation
                    {(() => {
                      const latestAction =
                        secureInfoData?.kycReviewHistory?.find(
                          (entry) => entry.action !== "NOTE"
                        )?.action;
                      const currentStatus =
                        secureInfoData?.kycStatus === "FAILED" &&
                        latestAction === "APPROVED"
                          ? "PASSED"
                          : secureInfoData?.kycStatus;

                      if (!currentStatus) return null;

                      return (
                        <Chip
                          label={currentStatus}
                          size="small"
                          color={
                            currentStatus === "PASSED"
                              ? "success"
                              : currentStatus === "FAILED" ||
                                  currentStatus === "REJECTED"
                                ? "error"
                                : "warning"
                          }
                          sx={{
                            height: 18,
                            fontSize: "0.65rem",
                            fontWeight: "bold",
                          }}
                        />
                      );
                    })()}
                  </Box>
                }
              />
            </Tabs>

            {isLoadingSecureInfo ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 300,
                }}
              >
                <CircularProgress color="inherit" />
              </Box>
            ) : isErrorSecureInfo ? (
              <Typography color="error" sx={{ p: 2 }}>
                {Labels.getLabel(
                  "memberSecureInfo.errorGettingInfo",
                  "There was an error getting the secure information"
                )}
              </Typography>
            ) : (
              <Box>
                {/* ── TAB 0: MEMBER PROFILE ── */}
                {tabValue === 0 && (
                  <Grid container spacing={3} sx={{ p: 1 }}>
                    {/* Personal Information Section */}
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="primary"
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <AssignmentIndIcon sx={{ mr: 1, fontSize: "1.1rem" }} />
                        Personal Information
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, bgcolor: "#f8faff" }}
                      >
                        <Grid container spacing={2}>
                          {/* Email */}
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Email Address
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <EmailIcon
                                sx={{
                                  fontSize: "0.9rem",
                                  color: "text.secondary",
                                }}
                              />
                              <Typography variant="body2">
                                {secureInfoData?.email || "-"}
                              </Typography>
                            </Box>
                          </Grid>
                          {/* Phone Number */}
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Phone Number
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <PhoneIcon
                                sx={{
                                  fontSize: "0.9rem",
                                  color: "text.secondary",
                                }}
                              />
                              <Typography variant="body2">
                                {secureInfoData?.phoneNumber ||
                                  secureInfoData?.homPhoneNumber ||
                                  "-"}
                              </Typography>
                            </Box>
                          </Grid>
                          {/* Date of Birth */}
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Date of Birth
                            </Typography>
                            {isEditingDOB ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <TextField
                                  size="small"
                                  value={tempDateOfBirth}
                                  onChange={(e) =>
                                    setTempDateOfBirth(e.target.value)
                                  }
                                  placeholder="YYYY-MM-DD"
                                  fullWidth
                                />
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={handleSaveDOB}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={handleCancelDOB}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Typography variant="body2">
                                  {secureInfoData?.dateOfBirth || "-"}
                                </Typography>
                                {canEditDOB && (
                                  <IconButton
                                    size="small"
                                    onClick={handleEditDOB}
                                    sx={{ ml: 0.5 }}
                                  >
                                    <EditIcon sx={{ fontSize: "1rem" }} />
                                  </IconButton>
                                )}
                              </Box>
                            )}
                          </Grid>
                          {/* Identification */}
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {"Identification"}
                            </Typography>
                            {secureInfoData?.identification &&
                            secureInfoData?.identifications?.length > 0 ? (
                              <Box sx={{ mt: 0.5 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {getIdentificationTypeLabel(
                                    secureInfoData?.identificationType
                                  ) && (
                                    <Chip
                                      size="small"
                                      color="default"
                                      variant="outlined"
                                      label={getIdentificationTypeLabel(
                                        secureInfoData?.identificationType
                                      )}
                                    />
                                  )}
                                  <Typography variant="body2">
                                    {secureInfoData?.identification || "-"}
                                  </Typography>
                                  {canEditIdentification && (
                                    <IconButton
                                      size="small"
                                      onClick={handleEditIdentification}
                                      sx={{ ml: 0.5 }}
                                    >
                                      <EditIcon sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                  )}
                                  <Typography
                                    variant="caption"
                                    color="primary"
                                    onClick={() =>
                                      setShowMoreIdentifications(
                                        !showMoreIdentifications
                                      )
                                    }
                                    sx={{
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                      ml: 0.5,
                                    }}
                                  >
                                    {showMoreIdentifications
                                      ? "Hide docs"
                                      : `+${secureInfoData.identifications.length} more doc${secureInfoData.identifications.length > 1 ? "s" : ""}`}
                                  </Typography>
                                </Box>
                                <Collapse in={showMoreIdentifications}>
                                  <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                                    {secureInfoData.identifications.map(
                                      (item, index) => {
                                        const jurisdiction =
                                          getIdentificationJurisdiction(item);

                                        return (
                                          <Box
                                            key={`${item.documentType || "id"}-${item.documentId || index}-${index}`}
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 0.75,
                                              flexWrap: "wrap",
                                            }}
                                          >
                                            <Chip
                                              size="small"
                                              color="default"
                                              variant="outlined"
                                              label={
                                                getIdentificationTypeLabel(
                                                  item.documentType
                                                ) || "Identification"
                                              }
                                            />
                                            {jurisdiction && (
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                              >
                                                ({jurisdiction})
                                              </Typography>
                                            )}
                                            <Typography variant="body2">
                                              {item.documentId || "-"}
                                            </Typography>
                                          </Box>
                                        );
                                      }
                                    )}
                                  </Stack>
                                </Collapse>
                              </Box>
                            ) : secureInfoData?.identifications?.length > 0 ? (
                              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                {secureInfoData.identifications.map(
                                  (item, index) => {
                                    const jurisdiction =
                                      getIdentificationJurisdiction(item);

                                    return (
                                      <Box
                                        key={`${item.documentType || "id"}-${item.documentId || index}-${index}`}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.75,
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        <Chip
                                          size="small"
                                          color="default"
                                          variant="outlined"
                                          label={
                                            getIdentificationTypeLabel(
                                              item.documentType
                                            ) || "Identification"
                                          }
                                        />
                                        {jurisdiction && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            ({jurisdiction})
                                          </Typography>
                                        )}
                                        <Typography variant="body2">
                                          {item.documentId || "-"}
                                        </Typography>
                                      </Box>
                                    );
                                  }
                                )}
                              </Stack>
                            ) : isEditingIdentification ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <TextField
                                  size="small"
                                  value={tempIdentification}
                                  onChange={(e) =>
                                    setTempIdentification(e.target.value)
                                  }
                                  placeholder="XXX-XX-XXXX"
                                  fullWidth
                                />
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={handleSaveIdentification}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={handleCancelIdentification}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  flexWrap: "wrap",
                                }}
                              >
                                {getIdentificationTypeLabel(
                                  secureInfoData?.identificationType
                                ) && (
                                  <Chip
                                    size="small"
                                    color="default"
                                    variant="outlined"
                                    label={getIdentificationTypeLabel(
                                      secureInfoData?.identificationType
                                    )}
                                  />
                                )}
                                <Typography variant="body2">
                                  {secureInfoData?.identification || "-"}
                                </Typography>
                                {canEditIdentification && (
                                  <IconButton
                                    size="small"
                                    onClick={handleEditIdentification}
                                    sx={{ ml: 0.5 }}
                                  >
                                    <EditIcon sx={{ fontSize: "1rem" }} />
                                  </IconButton>
                                )}
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>

                    {/* Address Information */}
                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            color="primary"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <HomeIcon sx={{ mr: 1, fontSize: "1.1rem" }} />
                            Physical Address
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="body2">
                              <strong>Street:</strong>{" "}
                              {secureInfoData?.physicalAddressMain || "-"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Unit:</strong>{" "}
                              {secureInfoData?.physicalAddressUnit || "-"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>City:</strong>{" "}
                              {secureInfoData?.physicalAddressCity || "-"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>State:</strong>{" "}
                              {secureInfoData?.physicalAddressState || "-"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Zip:</strong>{" "}
                              {secureInfoData?.physicalAddressZip || "-"}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            color="primary"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <HomeIcon sx={{ mr: 1, fontSize: "1.1rem" }} />
                            Mailing Address
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="body2">
                              <strong>Street:</strong>{" "}
                              {secureInfoData?.mailingAddressMain || "-"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Unit:</strong>{" "}
                              {secureInfoData?.mailingAddressUnit || "-"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>City:</strong>{" "}
                              {secureInfoData?.mailingAddressCity || "-"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>State:</strong>{" "}
                              {secureInfoData?.mailingAddressState || "-"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Zip:</strong>{" "}
                              {secureInfoData?.mailingAddressZip || "-"}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Accepted Terms */}
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="primary"
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <EditNoteIcon sx={{ mr: 1, fontSize: "1.1rem" }} />
                        Accepted Agreements
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, bgcolor: "#fafafa" }}
                      >
                        {secureInfoData?.acceptedTerms ? (
                          secureInfoData.acceptedTerms
                            .split(";")
                            .filter((t) => t.trim())
                            .map((term, i) => (
                              <Box
                                key={i}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <Typography variant="body2">
                                  • {term.trim()}
                                </Typography>
                                {secureInfoData?.acceptedTermsDate && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    ({secureInfoData.acceptedTermsDate})
                                  </Typography>
                                )}
                              </Box>
                            ))
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                )}

                {/* ── TAB 1: COMPLIANCE & AUDIT ── */}
                {tabValue === 1 && (
                  <Box sx={{ p: 1 }}>
                    {/* ── 1. Case Summary Card ── */}
                    {(() => {
                      const kycStarted = parseDatabaseDate(
                        secureInfoData?.kycStartedAt
                      );
                      const history = secureInfoData?.kycReviewHistory || [];
                      const lastEntry = history[0]; // already ordered desc
                      const lastActivityDate = lastEntry
                        ? parseDatabaseDate(lastEntry.createdAt)
                        : kycStarted;
                      const lastActivityActor = lastEntry
                        ? lastEntry.actor
                        : "System";
                      const lastActivityAction = lastEntry
                        ? lastEntry.action
                        : "KYC Check Initiated";

                      // Derive failure reason and pending docs from history (source of truth)
                      const lastRejectedEntry = history.find(
                        (e) => e.action === "REJECTED" || e.action === "FAILED"
                      );
                      const failureReason = lastRejectedEntry?.notes;
                      const lastEntryWithDocs = history.find(
                        (e) => e.requestedDocuments
                      );
                      const pendingDocs =
                        lastEntryWithDocs?.requestedDocuments ||
                        secureInfoData?.kycRequestedDocuments;

                      // Normalize and derive status with safety check
                      const dbStatus = secureInfoData?.kycStatus;
                      const latestAction = history.find(
                        (entry) => entry.action !== "NOTE"
                      )?.action;
                      const statusLabel =
                        dbStatus === "FAILED" && latestAction === "APPROVED"
                          ? "PASSED"
                          : dbStatus;

                      const isPassed = statusLabel === "PASSED";
                      const isFailed =
                        statusLabel === "FAILED" ||
                        statusLabel === "REJECTED" ||
                        statusLabel === "PENDING_SCAN_REVIEW";
                      const isReviewable =
                        isFailed ||
                        [
                          "KYC_MORE_INFO",
                          "DOC_SUBMITTED",
                          "DOCS_SUBMITTED",
                          "DOCS_UPLOADED",
                          "SCAN_SUBMITTED",
                        ].includes(statusLabel);

                      const statusColor = isPassed
                        ? "success"
                        : isFailed
                          ? "error"
                          : "warning";
                      const lastAutoEntry = history.find(
                        (e) =>
                          e.action !== "NOTE" &&
                          (e.actor?.toLowerCase() === "system" ||
                            e.actor?.toLowerCase() === "automated" ||
                            !e.actor)
                      );
                      const autoStatus = lastAutoEntry
                        ? lastAutoEntry.action
                        : dbStatus;
                      const decisionFactors =
                        secureInfoData?.kycDecisionFactors || {};
                      const decisionFactorLabels = {
                        tier: "Decision Tier",
                        wasIdLocated: "ID Located",
                        wasScanValid: "Scan Valid",
                        addressQualifierDetected: "Address Qualifier",
                        conditionalApprovalQualifierDetected:
                          "Conditional Approval Qualifier",
                        kycRestricted: "KYC Restricted",
                        tier1OverrideQualifierDetected: "Tier 1 Override",
                        plus90Rule: "+90 Rule",
                      };
                      const isSsnIdentity =
                        String(secureInfoData?.identificationType || "") ===
                        "1";
                      const hideFalseDecisionFactors = new Set([
                        "addressQualifierDetected",
                        "conditionalApprovalQualifierDetected",
                        "kycRestricted",
                        "tier1OverrideQualifierDetected",
                      ]);
                      const decisionFactorEntries = Object.entries(
                        decisionFactors
                      ).filter(
                        ([factor, value]) =>
                          factor !== "requiredDocuments" &&
                          factor !== "tier" &&
                          factor !== "tier1OverrideQualifierKeys" &&
                          !(isSsnIdentity && factor === "wasScanValid") &&
                          value !== null &&
                          value !== undefined &&
                          (typeof value !== "string" || value.trim() !== "") &&
                          !(
                            hideFalseDecisionFactors.has(factor) &&
                            value === false
                          )
                      );

                      const lastDocsRequestIdx = history.findIndex(
                        (e) => e.requestedDocuments
                      );
                      const lastDocsUploadIdx = history.findIndex(
                        (e) =>
                          e.action === "DOCS_UPLOADED" ||
                          e.action === "CUSTOMER_SUBMITTED"
                      );
                      const areDocsPending =
                        lastDocsRequestIdx !== -1 &&
                        (lastDocsUploadIdx === -1 ||
                          lastDocsUploadIdx > lastDocsRequestIdx);
                      const isMoreInfoStatus = [
                        "KYC_MORE_INFO",
                        "KYC_ONGOING",
                      ].includes(statusLabel);
                      const areAutomaticDocsPending =
                        Boolean(pendingDocs) &&
                        isMoreInfoStatus &&
                        lastDocsRequestIdx === -1;
                      const isWaitingForCustomerDocuments =
                        Boolean(pendingDocs) &&
                        (areDocsPending || areAutomaticDocsPending);

                      const currentState = isPassed
                        ? "KYC Evaluation Passed"
                        : latestAction === "REJECTED"
                          ? "KYC Evaluation Rejected"
                          : isWaitingForCustomerDocuments
                            ? "Waiting for Customer Documents"
                            : isFailed
                              ? "Pending Manual Review"
                              : statusLabel === "PENDING_REVIEW"
                                ? "Awaiting Compliance Decision"
                                : "In Progress";
                      const isTruthyFlag = (value) =>
                        value === true ||
                        String(value || "").toLowerCase() === "true";
                      const isKycRestricted = isTruthyFlag(
                        secureInfoData?.kycRestricted ||
                          decisionFactors.kycRestricted
                      );
                      const isKycHardFailed = isTruthyFlag(
                        secureInfoData?.kycHardFailed
                      );
                      return (
                        <Paper
                          variant="outlined"
                          sx={{
                            mb: 2,
                            overflow: "hidden",
                            borderLeft: 5,
                            borderLeftColor: `${statusColor}.main`,
                          }}
                        >
                          {/* Header row - Simplified */}
                          <Box
                            sx={{
                              px: 2,
                              pt: 1.5,
                              pb: 1,
                              borderBottom: 1,
                              borderColor: "divider",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                flexWrap: "wrap",
                              }}
                            >
                              <AssessmentIcon color="primary" />
                              <Typography variant="subtitle1" fontWeight="bold">
                                Current Case Summary
                              </Typography>
                              {isKycRestricted && !isPassed && (
                                <Chip
                                  icon={<BlockIcon />}
                                  label="Restricted"
                                  size="small"
                                  color="warning"
                                  variant="filled"
                                  sx={{
                                    fontWeight: "bold",
                                    height: 22,
                                    "& .MuiChip-icon": {
                                      fontSize: "0.95rem",
                                    },
                                  }}
                                />
                              )}
                              {isKycHardFailed && (
                                <Chip
                                  icon={<WarningAmberIcon />}
                                  label="Hard Failed"
                                  size="small"
                                  color="error"
                                  variant="filled"
                                  sx={{
                                    fontWeight: "bold",
                                    height: 22,
                                    "& .MuiChip-icon": {
                                      fontSize: "0.95rem",
                                    },
                                  }}
                                />
                              )}
                            </Box>
                            {isReviewable &&
                              latestAction !== "REJECTED" &&
                              latestAction !== "APPROVED" &&
                              UserService.hasRole(["compliance-mgr"]) && (
                                <Button
                                  variant="contained"
                                  color="warning"
                                  size="small"
                                  onClick={() => setKycReviewOpen(true)}
                                >
                                  Action Required: Review KYC
                                </Button>
                              )}
                            {UserService.hasRole(["it-super-admin"]) &&
                              secureInfoData?.kycWorkflowId &&
                              (isPassed || isFailed) && (
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  startIcon={<RefreshIcon />}
                                  disabled={kycRepairMutation.isLoading}
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        "Start a new KYC evaluation for this member? The assigned card will be preserved."
                                      )
                                    ) {
                                      kycRepairMutation.mutate();
                                    }
                                  }}
                                >
                                  Reprocess KYC
                                </Button>
                              )}
                          </Box>

                          {/* Four-column detail grid */}
                          <Grid container sx={{ p: 2 }} spacing={2}>
                            {/* Col 1: Automatic KYC Result */}
                            <Grid
                              item
                              xs={2.7}
                              sx={{ borderRight: 1, borderColor: "divider" }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="bold"
                                sx={{
                                  display: "block",
                                  mb: 1,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                }}
                              >
                                Automatic KYC Result
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Reference ID
                                  </Typography>
                                  <Typography variant="body2" fontWeight="500">
                                    {secureInfoData?.kycReferenceId ? (
                                      <Typography
                                        component="a"
                                        href={`https://web.idologylive.com/reports/query.php?id=${secureInfoData.kycReferenceId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="inherit"
                                        sx={{
                                          display: "inline",
                                          fontFamily: "monospace",
                                          fontWeight: "bold",
                                          color: "primary.main",
                                          textDecoration: "underline",
                                          cursor: "pointer",
                                          mr: 0.5,
                                        }}
                                      >
                                        {secureInfoData.kycReferenceId}
                                      </Typography>
                                    ) : (
                                      "N/A"
                                    )}
                                  </Typography>
                                </Box>
                                {decisionFactorEntries.length > 0 && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Decision Factors
                                    </Typography>
                                    <Grid
                                      container
                                      spacing={0.75}
                                      sx={{ mt: 0.25 }}
                                    >
                                      {decisionFactors.tier && (
                                        <Grid item xs={12}>
                                          {(() => {
                                            const tierNumber = String(
                                              decisionFactors.tier
                                            ).match(/TIER_(\d+)/i)?.[1];
                                            return (
                                              <Chip
                                                label="Tier"
                                                icon={
                                                  <Box
                                                    component="span"
                                                    sx={{
                                                      width: 20,
                                                      height: 20,
                                                      borderRadius: "50%",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      fontSize: "0.7rem",
                                                      fontWeight: "bold",
                                                      bgcolor:
                                                        tierNumber === "1"
                                                          ? "#a5d6a7"
                                                          : tierNumber === "2"
                                                            ? "#81c784"
                                                            : "#90caf9",
                                                      color:
                                                        tierNumber === "1"
                                                          ? "#1b5e20"
                                                          : tierNumber === "2"
                                                            ? "#1b5e20"
                                                            : "#0d47a1",
                                                    }}
                                                  >
                                                    {tierNumber || "?"}
                                                  </Box>
                                                }
                                                sx={{
                                                  bgcolor: "#f1f3f4",
                                                  color: "text.primary",
                                                  fontWeight: "bold",
                                                }}
                                              />
                                            );
                                          })()}
                                        </Grid>
                                      )}
                                      {decisionFactorEntries.map(
                                        ([factor, value]) => {
                                          const isBoolean =
                                            typeof value === "boolean";
                                          const isAddressQualifier =
                                            factor ===
                                            "addressQualifierDetected";
                                          const isConditionalApprovalQualifier =
                                            factor ===
                                            "conditionalApprovalQualifierDetected";
                                          const isRestricted =
                                            factor === "kycRestricted" &&
                                            isTruthyFlag(value);
                                          const isTier = factor === "tier";
                                          const tierNumber = isTier
                                            ? String(value || "").match(
                                                /TIER_(\d+)/i
                                              )?.[1]
                                            : null;
                                          const displayValue = isBoolean
                                            ? value
                                              ? "Yes"
                                              : "No"
                                            : isTier && tierNumber
                                              ? tierNumber
                                              : value || "N/A";
                                          return (
                                            <Grid item xs={12} key={factor}>
                                              <Chip
                                                label={
                                                  isRestricted
                                                    ? "Restricted"
                                                    : isBoolean
                                                      ? decisionFactorLabels[
                                                          factor
                                                        ] || factor
                                                      : isTier
                                                        ? "Tier"
                                                        : `${
                                                            decisionFactorLabels[
                                                              factor
                                                            ] || factor
                                                          }: ${displayValue}`
                                                }
                                                icon={
                                                  isRestricted ? (
                                                    <BlockIcon fontSize="small" />
                                                  ) : isTier ? (
                                                    <Box
                                                      component="span"
                                                      sx={{
                                                        width: 20,
                                                        height: 20,
                                                        borderRadius: "50%",
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                          "center",
                                                        fontSize: "0.7rem",
                                                        fontWeight: "bold",
                                                        bgcolor:
                                                          tierNumber === "1"
                                                            ? "#a5d6a7"
                                                            : "rgba(255,255,255,0.55)",
                                                        color:
                                                          tierNumber === "1"
                                                            ? "#1b5e20"
                                                            : "inherit",
                                                      }}
                                                    >
                                                      {tierNumber || "?"}
                                                    </Box>
                                                  ) : isBoolean ? (
                                                    (isAddressQualifier ||
                                                      isConditionalApprovalQualifier) &&
                                                    value ? (
                                                      <WarningAmberIcon fontSize="small" />
                                                    ) : value ? (
                                                      <CheckCircleOutlineIcon fontSize="small" />
                                                    ) : (
                                                      <CancelIcon fontSize="small" />
                                                    )
                                                  ) : undefined
                                                }
                                                size="small"
                                                color={
                                                  isRestricted
                                                    ? "warning"
                                                    : isTier
                                                      ? tierNumber === "1"
                                                        ? undefined
                                                        : tierNumber === "2"
                                                          ? "success"
                                                          : "info"
                                                      : isBoolean
                                                        ? (isAddressQualifier ||
                                                            isConditionalApprovalQualifier) &&
                                                          value
                                                          ? "warning"
                                                          : value
                                                            ? "success"
                                                            : "error"
                                                        : "info"
                                                }
                                                variant="filled"
                                                sx={{
                                                  maxWidth: "100%",
                                                  height: 24,
                                                  fontSize: "0.75rem",
                                                  fontWeight: "bold",
                                                  ...(isTier &&
                                                  tierNumber === "1"
                                                    ? {
                                                        bgcolor: "transparent",
                                                      }
                                                    : {}),
                                                  ...(isTier
                                                    ? {
                                                        height: 24,
                                                        padding: 0,
                                                        bgcolor: "transparent",
                                                        "& .MuiChip-label": {
                                                          display: "block",
                                                          order: 1,
                                                          paddingLeft: 0,
                                                          paddingRight: 4,
                                                        },
                                                        "& .MuiChip-icon": {
                                                          order: 2,
                                                          marginLeft: 0,
                                                          marginRight: 0,
                                                        },
                                                      }
                                                    : {}),
                                                  "& .MuiChip-label": {
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                  },
                                                }}
                                              />
                                            </Grid>
                                          );
                                        }
                                      )}
                                    </Grid>
                                  </Box>
                                )}
                                {isFailed && failureReason && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Failure Detail
                                    </Typography>
                                    {(() => {
                                      const isNoiseMessage = (value) =>
                                        /not eligible for questions/i.test(
                                          String(value || "")
                                        );
                                      const isRepeatedStatus = (value) => {
                                        const normalized = String(value || "")
                                          .trim()
                                          .toUpperCase();
                                        if (!normalized) {
                                          return false;
                                        }
                                        return [
                                          "FAILED",
                                          "FAIL",
                                          "REJECTED",
                                          "PASSED",
                                          "APPROVED",
                                          "PENDING_SCAN_REVIEW",
                                          String(autoStatus || "")
                                            .trim()
                                            .toUpperCase(),
                                          String(statusLabel || "")
                                            .trim()
                                            .toUpperCase(),
                                        ].includes(normalized);
                                      };

                                      // Prefer structured parser when entry contains response/details.
                                      const parsedStructured =
                                        parseStructuredScanPending(
                                          lastRejectedEntry
                                        );
                                      if (parsedStructured) {
                                        const filteredQualifiers = (
                                          parsedStructured.qualifiers || []
                                        ).filter(
                                          (qual) =>
                                            !isNoiseMessage(
                                              qual?.message || qual?.key
                                            )
                                        );

                                        return (
                                          <Box
                                            sx={{
                                              p: 0.75,
                                              mt: 0.25,
                                              bgcolor: "#ffebee",
                                              border: "1px solid #e57373",
                                              borderRadius: 1,
                                            }}
                                          >
                                            <Stack spacing={0.35}>
                                              {parsedStructured.summary
                                                ?.message &&
                                                !isRepeatedStatus(
                                                  parsedStructured.summary
                                                    .message
                                                ) &&
                                                !isNoiseMessage(
                                                  parsedStructured.summary
                                                    .message
                                                ) && (
                                                  <Chip
                                                    label={
                                                      parsedStructured.summary
                                                        .message
                                                    }
                                                    size="small"
                                                    color="error"
                                                    sx={{
                                                      width: "fit-content",
                                                      height: 18,
                                                      fontSize: "0.65rem",
                                                    }}
                                                  />
                                                )}

                                              {parsedStructured.results
                                                ?.message &&
                                                !isNoiseMessage(
                                                  parsedStructured.results
                                                    .message
                                                ) && (
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      color: "#b71c1c",
                                                      fontWeight: "bold",
                                                      lineHeight: 1.2,
                                                    }}
                                                  >
                                                    {
                                                      parsedStructured.results
                                                        .message
                                                    }
                                                  </Typography>
                                                )}

                                              {parsedStructured.idliveqError
                                                ?.message &&
                                                !isNoiseMessage(
                                                  parsedStructured.idliveqError
                                                    .message
                                                ) && (
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      color: "#b71c1c",
                                                      fontWeight: "bold",
                                                      lineHeight: 1.2,
                                                    }}
                                                  >
                                                    {
                                                      parsedStructured
                                                        .idliveqError.message
                                                    }
                                                  </Typography>
                                                )}

                                              {filteredQualifiers.length >
                                                0 && (
                                                <Box>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      display: "block",
                                                      color: "#b71c1c",
                                                      fontWeight: "bold",
                                                      mb: 0.25,
                                                    }}
                                                  >
                                                    Findings:
                                                  </Typography>
                                                  <Stack spacing={0.25}>
                                                    {filteredQualifiers.map(
                                                      (qual, qIdx) => (
                                                        <Typography
                                                          key={`summary-failure-qual-${qIdx}`}
                                                          variant="caption"
                                                          sx={{
                                                            color: "#b71c1c",
                                                            lineHeight: 1.2,
                                                          }}
                                                        >
                                                          •{" "}
                                                          {qual.message ||
                                                            qual.key}
                                                        </Typography>
                                                      )
                                                    )}
                                                  </Stack>
                                                </Box>
                                              )}
                                            </Stack>
                                          </Box>
                                        );
                                      }

                                      // Fallback to Automatic KYC Result legacy text parsing.
                                      const parsed =
                                        parseAutomaticKycResult(failureReason);
                                      if (!parsed) {
                                        return (
                                          <Typography
                                            variant="body2"
                                            color="error.main"
                                            sx={{ fontWeight: 500, mt: 0.5 }}
                                          >
                                            {failureReason}
                                          </Typography>
                                        );
                                      }
                                      return (
                                        <Box
                                          sx={{
                                            p: 0.75,
                                            mt: 0.25,
                                            bgcolor: "#ffebee",
                                            border: "1px solid #e57373",
                                            borderRadius: 1,
                                          }}
                                        >
                                          <Stack spacing={0.35}>
                                            {/* Error Message */}
                                            {parsed.errorMessage &&
                                              !isRepeatedStatus(
                                                parsed.errorMessage
                                              ) && (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontWeight: "bold",
                                                    color: "#c62828",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.4px",
                                                    lineHeight: 1.15,
                                                  }}
                                                >
                                                  ⚡ {parsed.errorMessage}
                                                </Typography>
                                              )}

                                            {/* Status Badge */}
                                            {parsed.summaryMessage &&
                                              !isRepeatedStatus(
                                                parsed.summaryMessage
                                              ) && (
                                                <Chip
                                                  label={parsed.summaryMessage}
                                                  size="small"
                                                  color="error"
                                                  sx={{
                                                    width: "fit-content",
                                                    height: 18,
                                                    fontSize: "0.65rem",
                                                  }}
                                                />
                                              )}

                                            {/* Results */}
                                            {parsed.resultsMessage && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: "#b71c1c",
                                                  fontWeight: "bold",
                                                  lineHeight: 1.2,
                                                }}
                                              >
                                                {parsed.resultsMessage}
                                              </Typography>
                                            )}

                                            {/* ID Live Q Error */}
                                            {parsed.idLiveQMessage && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: "#b71c1c",
                                                  fontWeight: "bold",
                                                  lineHeight: 1.2,
                                                }}
                                              >
                                                {parsed.idLiveQMessage}
                                              </Typography>
                                            )}
                                          </Stack>
                                        </Box>
                                      );
                                    })()}
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            {/* Col 2: Current Evaluation State */}
                            <Grid
                              item
                              xs={2.7}
                              sx={{ borderRight: 1, borderColor: "divider" }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="bold"
                                sx={{
                                  display: "block",
                                  mb: 1,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                }}
                              >
                                Current State
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Evaluation Stage
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {currentState}
                                  </Typography>
                                </Box>
                                {isWaitingForCustomerDocuments &&
                                  pendingDocs && (
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Waiting for
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="500"
                                        color="warning.dark"
                                      >
                                        {pendingDocs
                                          .split(",")
                                          .map((document) => document.trim())
                                          .filter(Boolean)
                                          .map((document, index) => (
                                            <Box key={`${document}-${index}`}>
                                              {document}
                                            </Box>
                                          ))}
                                      </Typography>
                                    </Box>
                                  )}
                                {secureInfoData?.kycReviewedBy && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Last Reviewed By
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight="500"
                                    >
                                      {secureInfoData.kycReviewedBy}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            {/* Col 3: Case Timeline */}
                            <Grid item xs={2}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="bold"
                                sx={{
                                  display: "block",
                                  mb: 1,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                }}
                              >
                                Case Timeline
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Opened
                                  </Typography>
                                  <Typography variant="body2" fontWeight="500">
                                    {kycStarted ? (
                                      formatRelativeTime(
                                        kycStarted,
                                        new Date(relativeTimeNow)
                                      )
                                    ) : (
                                      <em style={{ color: "#9e9e9e" }}>
                                        Unknown
                                      </em>
                                    )}
                                  </Typography>
                                  {kycStarted && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        display: "block",
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      {formatLocalDateTime(kycStarted)}
                                    </Typography>
                                  )}
                                </Box>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Last Activity
                                  </Typography>
                                  <Typography variant="body2" fontWeight="500">
                                    {lastActivityDate ? (
                                      formatRelativeTime(
                                        lastActivityDate,
                                        new Date(relativeTimeNow)
                                      )
                                    ) : (
                                      <em style={{ color: "#9e9e9e" }}>None</em>
                                    )}
                                  </Typography>
                                  {lastActivityDate && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        display: "block",
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      {formatLocalDateTime(lastActivityDate)}
                                    </Typography>
                                  )}
                                  {lastActivityDate && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "text.secondary",
                                        display: "block",
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      {lastActivityAction} by{" "}
                                      {lastActivityActor}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Grid>
                            {secureInfoData?.products?.length > 0 && (
                              <Grid
                                item
                                xs={4.6}
                                sx={{
                                  borderLeft: 1,
                                  borderColor: "divider",
                                  pl: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    minWidth: 0,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="bold"
                                    sx={{
                                      textTransform: "uppercase",
                                      letterSpacing: 0.5,
                                    }}
                                  >
                                    External Profile (
                                    {secureInfoData.products.length})
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mt: 0.25 }}
                                  >
                                    Created:{" "}
                                    {formatExternalCreationDate(
                                      secureInfoData.externalCreationDate
                                    )}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    overflowX: "auto",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "grid",
                                      minWidth: 520,
                                      gridTemplateColumns:
                                        "minmax(0, 2fr) repeat(3, minmax(0, 1fr))",
                                      gap: 1,
                                      px: 1,
                                      py: 0.5,
                                      bgcolor: "action.hover",
                                    }}
                                  >
                                    {[
                                      "Name",
                                      "Start Date",
                                      "End Date",
                                      "Total",
                                    ].map((label) => (
                                      <Typography
                                        key={label}
                                        variant="caption"
                                        color="text.secondary"
                                        fontWeight="bold"
                                      >
                                        {label}
                                      </Typography>
                                    ))}
                                  </Box>
                                  <Box
                                    sx={{ maxHeight: 180, overflowY: "auto" }}
                                  >
                                    {secureInfoData.products.map(
                                      (product, index) => (
                                        <Box
                                          key={`${product.name}-${index}`}
                                          sx={{
                                            display: "grid",
                                            minWidth: 520,
                                            gridTemplateColumns:
                                              "minmax(0, 2fr) repeat(3, minmax(0, 1fr))",
                                            gap: 1,
                                            px: 1,
                                            py: 0.65,
                                            borderTop: 1,
                                            borderColor: "divider",
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            noWrap
                                            title={product.name || "-"}
                                          >
                                            {product.name || "-"}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            noWrap
                                          >
                                            {product.startDate || "-"}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            noWrap
                                          >
                                            {product.endDate || "-"}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            noWrap
                                          >
                                            {product.total || "-"}
                                          </Typography>
                                        </Box>
                                      )
                                    )}
                                  </Box>
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        </Paper>
                      );
                    })()}

                    {/* ── 2. KYC Documents (default open) ── */}
                    <Accordion
                      defaultExpanded
                      disableGutters
                      elevation={0}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        mb: 1,
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <DescriptionIcon
                            sx={{ fontSize: "1.1rem", color: "primary.main" }}
                          />
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            color="primary"
                          >
                            KYC Documents
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        {UserService.hasRole(["compliance-mgr"]) && (
                          <Box
                            sx={{
                              mb: 2,
                              display: "flex",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Tooltip title="Upload document">
                              <IconButton
                                color="secondary"
                                size="small"
                                onClick={() => setKycUploadDialogOpen(true)}
                                sx={{
                                  border: 1,
                                  borderColor: "secondary.main",
                                  borderRadius: 1,
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}

                        <KycDocumentsViewer
                          memberId={memberInfo?.memberId}
                          companyId={memberInfo?.companyId}
                          expanded={tabValue === 1}
                          onViewDocument={handleViewDocument}
                        />
                      </AccordionDetails>
                    </Accordion>

                    {/* ── 3. Audit History (collapsed by default) ── */}
                    <Accordion
                      disableGutters
                      elevation={0}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <HistoryIcon
                            sx={{ fontSize: "1.1rem", color: "primary.main" }}
                          />
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            color="primary"
                          >
                            Audit History Timeline
                          </Typography>
                          {secureInfoData?.kycReviewHistory?.length > 0 && (
                            <Chip
                              label={secureInfoData.kycReviewHistory.length}
                              size="small"
                              color="primary"
                              sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
                            />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {UserService.hasRole([
                          "compliance-mgr",
                          "pc-program-super-admin",
                        ]) && (
                          <AuditHistoryNoteForm
                            key={`${memberInfo?.companyId}:${memberInfo?.memberId}`}
                            memberId={memberInfo?.memberId}
                            companyId={memberInfo?.companyId}
                          />
                        )}
                        <Box
                          sx={{
                            position: "relative",
                            pl: 3,
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              left: 7,
                              top: 0,
                              bottom: 0,
                              width: 2,
                              bgcolor: "divider",
                            },
                          }}
                        >
                          {secureInfoData?.kycReviewHistory?.length > 0 ? (
                            secureInfoData.kycReviewHistory.map(
                              (entry, idx) => (
                                <Box
                                  key={idx}
                                  sx={{ position: "relative", mb: 3 }}
                                >
                                  <Avatar
                                    sx={{
                                      position: "absolute",
                                      left: -32,
                                      top: 0,
                                      width: 18,
                                      height: 18,
                                      bgcolor:
                                        entry.action === "APPROVED"
                                          ? "success.main"
                                          : entry.action === "REJECTED"
                                            ? "error.main"
                                            : "primary.main",
                                      fontSize: "0.6rem",
                                    }}
                                  >
                                    {" "}
                                  </Avatar>
                                  <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    color="text.primary"
                                  >
                                    {entry.action}{" "}
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ ml: 1 }}
                                    >
                                      {formatLocalDateTime(
                                        parseDatabaseDate(entry.createdAt)
                                      ) || entry.createdAt}
                                    </Typography>
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: "block",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    By: {entry.actor}
                                  </Typography>
                                  {entry.notes && (
                                    <>
                                      {entry.action === "KYC_SCAN_RESULT" ? (
                                        (() => {
                                          const parsed =
                                            parseStructuredScanResult(entry) ||
                                            parseKycScanResult(entry.notes);
                                          if (!parsed) {
                                            return (
                                              <Paper
                                                variant="outlined"
                                                sx={{
                                                  p: 1,
                                                  mt: 0.5,
                                                  bgcolor: "#fdfdfd",
                                                }}
                                              >
                                                <Typography variant="caption">
                                                  {entry.notes}
                                                </Typography>
                                              </Paper>
                                            );
                                          }
                                          return (
                                            <Paper
                                              variant="outlined"
                                              sx={{
                                                p: 2,
                                                mt: 0.5,
                                                bgcolor: "#f0f7ff",
                                                border: "2px solid #90caf9",
                                                borderRadius: 2,
                                                background:
                                                  "linear-gradient(135deg, #f0f7ff 0%, #e3f2fd 100%)",
                                                boxShadow:
                                                  "0 2px 8px rgba(144, 202, 249, 0.3)",
                                              }}
                                            >
                                              <Stack spacing={1}>
                                                {/* Scan + Overall Status */}
                                                {(parsed.idScanResult ||
                                                  parsed.idScanVerificationResult ||
                                                  parsed.idScanSummaryResult ||
                                                  parsed.overallResult ||
                                                  parsed.tamperResult ||
                                                  parsed.confidence) && (
                                                  <Box sx={{ mb: 1.5 }}>
                                                    <Typography
                                                      variant="caption"
                                                      fontWeight="bold"
                                                      color="primary"
                                                      sx={{
                                                        display: "block",
                                                        mb: 1,
                                                        fontSize: "0.85rem",
                                                        textTransform:
                                                          "uppercase",
                                                        letterSpacing: "0.5px",
                                                      }}
                                                    >
                                                      Scan Results
                                                    </Typography>
                                                    <Box
                                                      sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems:
                                                          "flex-start",
                                                        gap: 0.5,
                                                        pb: 1,
                                                        borderBottom:
                                                          "1px solid #e0e0e0",
                                                      }}
                                                    >
                                                      <Box
                                                        sx={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: 2,
                                                          flexWrap: "wrap",
                                                        }}
                                                      >
                                                        {parsed.idScanSummaryResult && (
                                                          <Box
                                                            sx={{
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                              gap: 0.75,
                                                            }}
                                                          >
                                                            <Typography
                                                              variant="caption"
                                                              fontWeight="bold"
                                                              color="text.secondary"
                                                            >
                                                              Summary
                                                            </Typography>
                                                            <Chip
                                                              label={
                                                                parsed
                                                                  .idScanSummaryResult
                                                                  .message ||
                                                                parsed
                                                                  .idScanSummaryResult
                                                                  .key
                                                              }
                                                              size="small"
                                                              color="info"
                                                            />
                                                          </Box>
                                                        )}
                                                        {parsed.idScanResult && (
                                                          <Box
                                                            sx={{
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                              gap: 0.75,
                                                            }}
                                                          >
                                                            <Typography
                                                              variant="caption"
                                                              fontWeight="bold"
                                                              color="text.secondary"
                                                            >
                                                              Scan
                                                            </Typography>
                                                            <Chip
                                                              label={
                                                                parsed
                                                                  .idScanResult
                                                                  .message ||
                                                                parsed
                                                                  .idScanResult
                                                                  .key
                                                              }
                                                              size="small"
                                                              color="primary"
                                                            />
                                                          </Box>
                                                        )}
                                                        {parsed.overallResult && (
                                                          <Box
                                                            sx={{
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                              gap: 0.75,
                                                            }}
                                                          >
                                                            <Typography
                                                              variant="caption"
                                                              fontWeight="bold"
                                                              color="text.secondary"
                                                            >
                                                              Overall Result
                                                            </Typography>
                                                            <Chip
                                                              label={
                                                                parsed.overallResult
                                                              }
                                                              size="small"
                                                              color={
                                                                parsed.overallResult ===
                                                                "Passed"
                                                                  ? "success"
                                                                  : "warning"
                                                              }
                                                            />
                                                          </Box>
                                                        )}
                                                        {parsed.tamperResult && (
                                                          <Box
                                                            sx={{
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                              gap: 0.75,
                                                            }}
                                                          >
                                                            <Typography
                                                              variant="caption"
                                                              fontWeight="bold"
                                                              color="text.secondary"
                                                            >
                                                              Tamper Check
                                                            </Typography>
                                                            <Chip
                                                              label={
                                                                parsed.tamperResult
                                                              }
                                                              size="small"
                                                              color={
                                                                parsed.tamperResult ===
                                                                "Passed"
                                                                  ? "success"
                                                                  : "warning"
                                                              }
                                                            />
                                                          </Box>
                                                        )}
                                                        {parsed.confidence && (
                                                          <Box
                                                            sx={{
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                              gap: 0.75,
                                                            }}
                                                          >
                                                            <Typography
                                                              variant="caption"
                                                              fontWeight="bold"
                                                              color="text.secondary"
                                                            >
                                                              Confidence
                                                            </Typography>
                                                            {(() => {
                                                              const confidenceValue =
                                                                Number(
                                                                  parsed.confidence
                                                                );
                                                              const isGreen =
                                                                confidenceValue >=
                                                                80;
                                                              const isYellow =
                                                                confidenceValue >=
                                                                  50 &&
                                                                confidenceValue <
                                                                  80;
                                                              const isRed =
                                                                confidenceValue <
                                                                50;
                                                              return (
                                                                <Box
                                                                  sx={{
                                                                    display:
                                                                      "flex",
                                                                    alignItems:
                                                                      "center",
                                                                    gap: 0.5,
                                                                  }}
                                                                >
                                                                  <Box
                                                                    sx={{
                                                                      width: 16,
                                                                      minWidth: 16,
                                                                      py: 0.5,
                                                                      px: 0.25,
                                                                      borderRadius: 1,
                                                                      bgcolor:
                                                                        "#263238",
                                                                      display:
                                                                        "flex",
                                                                      flexDirection:
                                                                        "column",
                                                                      alignItems:
                                                                        "center",
                                                                      gap: 0.25,
                                                                    }}
                                                                  >
                                                                    <Box
                                                                      sx={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius:
                                                                          "50%",
                                                                        bgcolor:
                                                                          isGreen
                                                                            ? "#2e7d32"
                                                                            : "rgba(46, 125, 50, 0.25)",
                                                                      }}
                                                                    />
                                                                    <Box
                                                                      sx={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius:
                                                                          "50%",
                                                                        bgcolor:
                                                                          isYellow
                                                                            ? "#ed6c02"
                                                                            : "rgba(237, 108, 2, 0.25)",
                                                                      }}
                                                                    />
                                                                    <Box
                                                                      sx={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius:
                                                                          "50%",
                                                                        bgcolor:
                                                                          isRed
                                                                            ? "#d32f2f"
                                                                            : "rgba(211, 47, 47, 0.25)",
                                                                      }}
                                                                    />
                                                                  </Box>
                                                                  <Typography
                                                                    variant="caption"
                                                                    fontWeight="bold"
                                                                  >
                                                                    {
                                                                      parsed.confidence
                                                                    }
                                                                    %
                                                                  </Typography>
                                                                </Box>
                                                              );
                                                            })()}
                                                          </Box>
                                                        )}
                                                      </Box>
                                                      {parsed.idScanVerificationResult && (
                                                        <Typography variant="caption">
                                                          <strong>OCR:</strong>{" "}
                                                          {parsed
                                                            .idScanVerificationResult
                                                            .message ||
                                                            parsed
                                                              .idScanVerificationResult
                                                              .key}
                                                        </Typography>
                                                      )}
                                                    </Box>
                                                  </Box>
                                                )}

                                                {/* Image Classification */}
                                                {(parsed.imageClassification
                                                  ?.front ||
                                                  parsed.imageClassification
                                                    ?.back ||
                                                  parsed.imageClassification
                                                    ?.overall) && (
                                                  <Box sx={{ mb: 1.5 }}>
                                                    <Typography
                                                      variant="caption"
                                                      fontWeight="bold"
                                                      color="primary"
                                                      sx={{
                                                        display: "block",
                                                        mb: 1,
                                                        fontSize: "0.85rem",
                                                        textTransform:
                                                          "uppercase",
                                                        letterSpacing: "0.5px",
                                                      }}
                                                    >
                                                      Image Classification
                                                    </Typography>
                                                    <Box
                                                      sx={{
                                                        display: "grid",
                                                        gridTemplateColumns:
                                                          "1fr 1fr 1fr",
                                                        gap: 0.5,
                                                        fontSize: "0.75rem",
                                                      }}
                                                    >
                                                      {parsed
                                                        .imageClassification
                                                        ?.front && (
                                                        <Typography variant="caption">
                                                          <strong>
                                                            Front:
                                                          </strong>{" "}
                                                          {parsed
                                                            .imageClassification
                                                            .front.name ||
                                                            parsed
                                                              .imageClassification
                                                              .front
                                                              .className ||
                                                            "Unknown"}
                                                        </Typography>
                                                      )}
                                                      {parsed
                                                        .imageClassification
                                                        ?.back && (
                                                        <Typography variant="caption">
                                                          <strong>Back:</strong>{" "}
                                                          {parsed
                                                            .imageClassification
                                                            .back.name ||
                                                            parsed
                                                              .imageClassification
                                                              .back.className ||
                                                            "Unknown"}
                                                        </Typography>
                                                      )}
                                                      {parsed
                                                        .imageClassification
                                                        ?.overall && (
                                                        <Typography variant="caption">
                                                          <strong>
                                                            Overall:
                                                          </strong>{" "}
                                                          {parsed
                                                            .imageClassification
                                                            .overall.name ||
                                                            parsed
                                                              .imageClassification
                                                              .overall
                                                              .className ||
                                                            "Unknown"}
                                                        </Typography>
                                                      )}
                                                    </Box>
                                                  </Box>
                                                )}

                                                {/* Identity Information */}
                                                {(parsed.locatedIdScanRecord
                                                  ?.firstName ||
                                                  parsed.locatedIdScanRecord
                                                    ?.lastName ||
                                                  parsed.locatedIdScanRecord
                                                    ?.dateOfBirth ||
                                                  parsed.firstName ||
                                                  parsed.lastName ||
                                                  parsed.dateOfBirth) && (
                                                  <Box sx={{ mb: 1.5 }}>
                                                    <Typography
                                                      variant="caption"
                                                      fontWeight="bold"
                                                      color="primary"
                                                      sx={{
                                                        display: "block",
                                                        mb: 1,
                                                        fontSize: "0.85rem",
                                                        textTransform:
                                                          "uppercase",
                                                        letterSpacing: "0.5px",
                                                      }}
                                                    >
                                                      👤 Identity Information
                                                    </Typography>
                                                    {(() => {
                                                      const identityRecord =
                                                        parsed.locatedIdScanRecord ||
                                                        {};
                                                      const displayFirstName =
                                                        identityRecord.firstName ||
                                                        parsed.firstName;
                                                      const displayMiddleName =
                                                        identityRecord.middleName ||
                                                        parsed.middleName;
                                                      const displayLastName =
                                                        identityRecord.lastName ||
                                                        parsed.lastName;
                                                      const displayDateOfBirth =
                                                        identityRecord.dateOfBirth ||
                                                        parsed.dateOfBirth;
                                                      return (
                                                        <Box
                                                          sx={{
                                                            display: "grid",
                                                            gridTemplateColumns:
                                                              "1fr 1fr",
                                                            gap: 0.5,
                                                            fontSize: "0.75rem",
                                                          }}
                                                        >
                                                          {displayFirstName && (
                                                            <Typography variant="caption">
                                                              <strong>
                                                                Name:
                                                              </strong>{" "}
                                                              {displayFirstName}
                                                              {displayMiddleName
                                                                ? ` ${displayMiddleName}`
                                                                : ""}{" "}
                                                              {displayLastName}
                                                            </Typography>
                                                          )}
                                                          {displayDateOfBirth && (
                                                            <Typography variant="caption">
                                                              <strong>
                                                                DOB:
                                                              </strong>{" "}
                                                              {
                                                                displayDateOfBirth
                                                              }
                                                            </Typography>
                                                          )}
                                                        </Box>
                                                      );
                                                    })()}
                                                  </Box>
                                                )}

                                                {/* Document Information */}
                                                {(parsed.locatedIdScanRecord
                                                  ?.documentType ||
                                                  parsed.locatedIdScanRecord
                                                    ?.documentNumber ||
                                                  parsed.locatedIdScanRecord
                                                    ?.expirationDate ||
                                                  parsed.locatedIdScanRecord
                                                    ?.dateOfIssuance ||
                                                  parsed.docType ||
                                                  parsed.docNumber ||
                                                  parsed.expirationDate ||
                                                  parsed.dateOfIssuance) && (
                                                  <Box sx={{ mb: 1.5 }}>
                                                    <Typography
                                                      variant="caption"
                                                      fontWeight="bold"
                                                      color="primary"
                                                      sx={{
                                                        display: "block",
                                                        mb: 1,
                                                        fontSize: "0.85rem",
                                                        textTransform:
                                                          "uppercase",
                                                        letterSpacing: "0.5px",
                                                      }}
                                                    >
                                                      📄 Document Information
                                                    </Typography>
                                                    {(() => {
                                                      const identityRecord =
                                                        parsed.locatedIdScanRecord ||
                                                        {};
                                                      const displayDocType =
                                                        identityRecord.documentType ||
                                                        parsed.docType;
                                                      const displayDocNumber =
                                                        identityRecord.documentNumber ||
                                                        parsed.docNumber;
                                                      const displayExpirationDate =
                                                        identityRecord.expirationDate ||
                                                        parsed.expirationDate;
                                                      const displayExpeditionDate =
                                                        identityRecord.dateOfIssuance ||
                                                        parsed.dateOfIssuance;
                                                      return (
                                                        <Box
                                                          sx={{
                                                            display: "grid",
                                                            gridTemplateColumns:
                                                              "1fr 1fr",
                                                            gap: 0.5,
                                                            fontSize: "0.75rem",
                                                          }}
                                                        >
                                                          {displayDocType && (
                                                            <Typography variant="caption">
                                                              <strong>
                                                                Type:
                                                              </strong>{" "}
                                                              {displayDocType}
                                                            </Typography>
                                                          )}
                                                          {displayDocNumber && (
                                                            <Typography variant="caption">
                                                              <strong>
                                                                Number:
                                                              </strong>{" "}
                                                              {displayDocNumber}
                                                            </Typography>
                                                          )}
                                                          {displayExpirationDate && (
                                                            <Typography variant="caption">
                                                              <strong>
                                                                Expiration:
                                                              </strong>{" "}
                                                              {
                                                                displayExpirationDate
                                                              }
                                                            </Typography>
                                                          )}
                                                          {displayExpeditionDate && (
                                                            <Typography variant="caption">
                                                              <strong>
                                                                Expedition:
                                                              </strong>{" "}
                                                              {
                                                                displayExpeditionDate
                                                              }
                                                            </Typography>
                                                          )}
                                                        </Box>
                                                      );
                                                    })()}
                                                  </Box>
                                                )}

                                                {/* Address Information */}
                                                {(parsed.locatedIdScanRecord
                                                  ?.streetNumber ||
                                                  parsed.locatedIdScanRecord
                                                    ?.streetName ||
                                                  parsed.locatedIdScanRecord
                                                    ?.city ||
                                                  parsed.locatedIdScanRecord
                                                    ?.state ||
                                                  parsed.locatedIdScanRecord
                                                    ?.zip ||
                                                  parsed.address
                                                    ?.streetNumber ||
                                                  parsed.address?.streetName ||
                                                  parsed.address?.city ||
                                                  parsed.address?.state ||
                                                  parsed.address?.zip) && (
                                                  <Box sx={{ mb: 1.5 }}>
                                                    <Typography
                                                      variant="caption"
                                                      fontWeight="bold"
                                                      color="primary"
                                                      sx={{
                                                        display: "block",
                                                        mb: 1,
                                                        fontSize: "0.85rem",
                                                        textTransform:
                                                          "uppercase",
                                                        letterSpacing: "0.5px",
                                                      }}
                                                    >
                                                      📍 Address Information
                                                    </Typography>
                                                    {(() => {
                                                      const identityRecord =
                                                        parsed.locatedIdScanRecord ||
                                                        {};
                                                      const displayStreetNumber =
                                                        identityRecord.streetNumber ||
                                                        parsed.address
                                                          ?.streetNumber;
                                                      const displayStreetName =
                                                        identityRecord.streetName ||
                                                        parsed.address
                                                          ?.streetName;
                                                      const displayCity =
                                                        identityRecord.city ||
                                                        parsed.address?.city;
                                                      const displayState =
                                                        identityRecord.state ||
                                                        parsed.address?.state;
                                                      const displayZip =
                                                        identityRecord.zip ||
                                                        parsed.address?.zip;
                                                      return (
                                                        <Box
                                                          sx={{
                                                            display: "flex",
                                                            flexDirection:
                                                              "column",
                                                            gap: 0.3,
                                                            fontSize: "0.75rem",
                                                          }}
                                                        >
                                                          {(displayStreetNumber ||
                                                            displayStreetName) && (
                                                            <Typography variant="caption">
                                                              <strong>
                                                                Street:
                                                              </strong>{" "}
                                                              {displayStreetNumber
                                                                ? `${displayStreetNumber} `
                                                                : ""}
                                                              {displayStreetName ||
                                                                ""}
                                                            </Typography>
                                                          )}
                                                          {(displayCity ||
                                                            displayState ||
                                                            displayZip) && (
                                                            <Typography variant="caption">
                                                              <strong>
                                                                City:
                                                              </strong>{" "}
                                                              {displayCity}
                                                              {displayState
                                                                ? `, ${displayState}`
                                                                : ""}
                                                              {displayZip
                                                                ? ` ${displayZip}`
                                                                : ""}
                                                            </Typography>
                                                          )}
                                                        </Box>
                                                      );
                                                    })()}
                                                  </Box>
                                                )}

                                                {/* Image Quality */}
                                                {(parsed.imageQuality?.front
                                                  ?.sharpness ||
                                                  parsed.imageQuality?.back
                                                    ?.sharpness) && (
                                                  <Box sx={{ mb: 1.5 }}>
                                                    <Typography
                                                      variant="caption"
                                                      fontWeight="bold"
                                                      color="primary"
                                                      sx={{
                                                        display: "block",
                                                        mb: 1,
                                                        fontSize: "0.85rem",
                                                        textTransform:
                                                          "uppercase",
                                                        letterSpacing: "0.5px",
                                                      }}
                                                    >
                                                      📸 Image Quality
                                                    </Typography>
                                                    <Box
                                                      sx={{
                                                        display: "grid",
                                                        gridTemplateColumns:
                                                          "1fr 1fr",
                                                        gap: 0.5,
                                                        fontSize: "0.75rem",
                                                      }}
                                                    >
                                                      {parsed.imageQuality
                                                        ?.front?.sharpness && (
                                                        <Typography variant="caption">
                                                          <strong>
                                                            Front Sharpness:
                                                          </strong>{" "}
                                                          {
                                                            parsed.imageQuality
                                                              .front.sharpness
                                                          }
                                                          % / Glare:{" "}
                                                          {
                                                            parsed.imageQuality
                                                              .front.glare
                                                          }
                                                          %
                                                        </Typography>
                                                      )}
                                                      {parsed.imageQuality?.back
                                                        ?.sharpness && (
                                                        <Typography variant="caption">
                                                          <strong>
                                                            Back Sharpness:
                                                          </strong>{" "}
                                                          {
                                                            parsed.imageQuality
                                                              .back.sharpness
                                                          }
                                                          % / Glare:{" "}
                                                          {
                                                            parsed.imageQuality
                                                              .back.glare
                                                          }
                                                          %
                                                        </Typography>
                                                      )}
                                                    </Box>
                                                  </Box>
                                                )}

                                                {/* Qualifiers */}
                                                {parsed.qualifiers &&
                                                  parsed.qualifiers.length >
                                                    0 && (
                                                    <Box sx={{ mb: 1.5 }}>
                                                      <Typography
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        color="warning"
                                                        sx={{
                                                          display: "block",
                                                          mb: 1,
                                                          fontSize: "0.85rem",
                                                          textTransform:
                                                            "uppercase",
                                                          letterSpacing:
                                                            "0.5px",
                                                        }}
                                                      >
                                                        ⚠ Qualifiers
                                                      </Typography>
                                                      <Stack spacing={0.75}>
                                                        {parsed.qualifiers.map(
                                                          (qual, qIdx) => (
                                                            <Box
                                                              key={qIdx}
                                                              sx={{
                                                                p: 0.75,
                                                                bgcolor:
                                                                  "#fff9c4",
                                                                border:
                                                                  "1px solid #fbc02d",
                                                                borderRadius: 0.5,
                                                              }}
                                                            >
                                                              <Typography
                                                                variant="caption"
                                                                sx={{
                                                                  display:
                                                                    "block",
                                                                  fontWeight:
                                                                    "bold",
                                                                  color:
                                                                    "#f57f17",
                                                                }}
                                                              >
                                                                {qual.message}
                                                              </Typography>
                                                              <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                  fontSize:
                                                                    "0.65rem",
                                                                }}
                                                              >
                                                                {qual.key}
                                                              </Typography>
                                                            </Box>
                                                          )
                                                        )}
                                                      </Stack>
                                                    </Box>
                                                  )}

                                                {/* Authentication Results */}
                                                {parsed.authResults &&
                                                  parsed.authResults.length >
                                                    0 && (
                                                    <Accordion
                                                      disableGutters
                                                      elevation={0}
                                                      sx={{
                                                        mb: 0.5,
                                                        bgcolor: "transparent",
                                                        "&:before": {
                                                          display: "none",
                                                        },
                                                      }}
                                                    >
                                                      <AccordionSummary
                                                        expandIcon={
                                                          <ExpandMoreIcon />
                                                        }
                                                        sx={{
                                                          minHeight: 32,
                                                          px: 0,
                                                          "& .MuiAccordionSummary-content":
                                                            {
                                                              my: 0.5,
                                                            },
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="caption"
                                                          fontWeight="bold"
                                                          color="primary"
                                                          sx={{
                                                            fontSize: "0.85rem",
                                                            textTransform:
                                                              "uppercase",
                                                            letterSpacing:
                                                              "0.5px",
                                                          }}
                                                        >
                                                          Authentication Checks
                                                        </Typography>
                                                      </AccordionSummary>
                                                      <AccordionDetails
                                                        sx={{ px: 0, pt: 0 }}
                                                      >
                                                        <Box
                                                          sx={{
                                                            display: "flex",
                                                            flexDirection:
                                                              "column",
                                                            gap: 0.75,
                                                          }}
                                                        >
                                                          {parsed.authResults.map(
                                                            (result, idx) => (
                                                              <Tooltip
                                                                key={idx}
                                                                title={
                                                                  <Box>
                                                                    <Typography
                                                                      variant="caption"
                                                                      sx={{
                                                                        display:
                                                                          "block",
                                                                        fontWeight:
                                                                          "bold",
                                                                        mb: 0.5,
                                                                      }}
                                                                    >
                                                                      {`${result.name || "Authentication Check"}${
                                                                        result.documentRegion
                                                                          ? ` (${result.documentRegion})`
                                                                          : ""
                                                                      }`}
                                                                    </Typography>
                                                                    {result.disposition && (
                                                                      <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                          display:
                                                                            "block",
                                                                          mb: 0.5,
                                                                        }}
                                                                      >
                                                                        {
                                                                          result.disposition
                                                                        }
                                                                      </Typography>
                                                                    )}
                                                                    <Typography
                                                                      variant="caption"
                                                                      sx={{
                                                                        fontSize:
                                                                          "0.7rem",
                                                                        whiteSpace:
                                                                          "pre-wrap",
                                                                      }}
                                                                    >
                                                                      {
                                                                        result.actions
                                                                      }
                                                                    </Typography>
                                                                  </Box>
                                                                }
                                                                placement="top"
                                                                arrow
                                                              >
                                                                <Box
                                                                  sx={{
                                                                    display:
                                                                      "flex",
                                                                    alignItems:
                                                                      "center",
                                                                    gap: 0.75,
                                                                    fontSize:
                                                                      "0.7rem",
                                                                    p: 0.75,
                                                                    borderRadius: 1,
                                                                    bgcolor:
                                                                      result.status ===
                                                                      "Passed"
                                                                        ? "#e8f5e9"
                                                                        : result.status ===
                                                                            "Failed"
                                                                          ? "#ffebee"
                                                                          : "#fff3e0",
                                                                    border:
                                                                      "1px solid",
                                                                    borderColor:
                                                                      result.status ===
                                                                      "Passed"
                                                                        ? "#81c784"
                                                                        : result.status ===
                                                                            "Failed"
                                                                          ? "#e57373"
                                                                          : "#ffb74d",
                                                                    cursor:
                                                                      "pointer",
                                                                    transition:
                                                                      "all 0.2s ease",
                                                                    "&:hover": {
                                                                      boxShadow:
                                                                        "0 2px 8px rgba(0,0,0,0.15)",
                                                                    },
                                                                  }}
                                                                >
                                                                  <Chip
                                                                    label={
                                                                      result.status
                                                                    }
                                                                    size="small"
                                                                    color={
                                                                      result.status ===
                                                                      "Passed"
                                                                        ? "success"
                                                                        : result.status ===
                                                                            "Failed"
                                                                          ? "error"
                                                                          : "warning"
                                                                    }
                                                                    sx={{
                                                                      height: 18,
                                                                      fontSize:
                                                                        "0.65rem",
                                                                      fontWeight:
                                                                        "bold",
                                                                    }}
                                                                  />
                                                                  <Box
                                                                    sx={{
                                                                      flex: 2,
                                                                      display:
                                                                        "flex",
                                                                    }}
                                                                  >
                                                                    <Typography
                                                                      variant="caption"
                                                                      sx={{
                                                                        display:
                                                                          "block",
                                                                      }}
                                                                    >
                                                                      {`${result.disposition} `}
                                                                    </Typography>
                                                                    <Typography
                                                                      variant="caption"
                                                                      sx={{
                                                                        display:
                                                                          "block",
                                                                        fontWeight:
                                                                          "bold",
                                                                        paddingLeft:
                                                                          "0.25rem",
                                                                      }}
                                                                    >
                                                                      {`${result.documentRegion ? ` (${result.documentRegion})` : ""}`}
                                                                    </Typography>
                                                                  </Box>
                                                                </Box>
                                                              </Tooltip>
                                                            )
                                                          )}
                                                        </Box>
                                                      </AccordionDetails>
                                                    </Accordion>
                                                  )}
                                              </Stack>
                                            </Paper>
                                          );
                                        })()
                                      ) : [
                                          "SCAN_PENDING_FAIL",
                                          "SCAN_PENDING_PASS",
                                          "PASSED",
                                          "PENDING_SCAN_REVIEW",
                                          "KYC_SCAN_PENDING_REVIEW",
                                          "FAILED",
                                        ].includes(entry.action) ? (
                                        (() => {
                                          const parsed =
                                            parseStructuredScanPending(entry) ||
                                            parseScanPendingFail(entry.notes) ||
                                            parseScanPendingPass(entry.notes);
                                          const normalizedReason = (
                                            parsed?.reason || ""
                                          ).toLowerCase();
                                          const duplicateFragments = [
                                            parsed?.summary?.message,
                                            parsed?.summary?.key,
                                            parsed?.results?.message,
                                            parsed?.results?.key,
                                            parsed?.idliveqError?.message,
                                            parsed?.idliveqError?.key,
                                          ]
                                            .filter(Boolean)
                                            .map((fragment) =>
                                              String(fragment).toLowerCase()
                                            );
                                          const shouldShowReason =
                                            Boolean(parsed?.reason) &&
                                            !duplicateFragments.every(
                                              (fragment) =>
                                                fragment.length === 0 ||
                                                normalizedReason.includes(
                                                  fragment
                                                )
                                            );
                                          if (!parsed) {
                                            return (
                                              <Paper
                                                variant="outlined"
                                                sx={{
                                                  p: 1,
                                                  mt: 0.5,
                                                  bgcolor: "#fdfdfd",
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    whiteSpace: "pre-wrap",
                                                    overflowWrap: "anywhere",
                                                  }}
                                                >
                                                  {entry.notes}
                                                </Typography>
                                              </Paper>
                                            );
                                          }
                                          const isPendingPass =
                                            entry.action ===
                                              "SCAN_PENDING_PASS" ||
                                            entry.action === "PASSED";
                                          return (
                                            <Paper
                                              variant="outlined"
                                              sx={{
                                                p: 2,
                                                mt: 0.5,
                                                bgcolor: isPendingPass
                                                  ? "#e8f5e9"
                                                  : "#fff3e0",
                                                border: isPendingPass
                                                  ? "2px solid #81c784"
                                                  : "2px solid #ffb74d",
                                                borderRadius: 2,
                                                background: isPendingPass
                                                  ? "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)"
                                                  : "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
                                                boxShadow: isPendingPass
                                                  ? "0 2px 8px rgba(129, 199, 132, 0.3)"
                                                  : "0 2px 8px rgba(255, 183, 77, 0.3)",
                                              }}
                                            >
                                              <Stack spacing={1.5}>
                                                {/* Scan or ID results */}
                                                <Box sx={{ mb: 1.5 }}>
                                                  <Typography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                    color={
                                                      isPendingPass
                                                        ? "success"
                                                        : "primary"
                                                    }
                                                    sx={{
                                                      display: "block",
                                                      mb: 1,
                                                      fontSize: "0.85rem",
                                                      textTransform:
                                                        "uppercase",
                                                      letterSpacing: "0.5px",
                                                    }}
                                                  >
                                                    {entry.action ===
                                                    "SCAN_PENDING_PASS"
                                                      ? "ID VERIFY RESULTS"
                                                      : "Scan Results"}
                                                  </Typography>
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      flexDirection: "column",
                                                      gap: 0.75,
                                                      pb: 1,
                                                      borderBottom:
                                                        isPendingPass
                                                          ? "1px solid #c8e6c9"
                                                          : "1px solid #ffe0b2",
                                                    }}
                                                  >
                                                    <Box
                                                      sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        flexWrap: "wrap",
                                                      }}
                                                    >
                                                      <Chip
                                                        label={
                                                          parsed.summary
                                                            ?.message ||
                                                          parsed.resultMessage ||
                                                          entry.action ||
                                                          "PENDING_SCAN_REVIEW"
                                                        }
                                                        color={
                                                          isPendingPass
                                                            ? "success"
                                                            : "warning"
                                                        }
                                                        size="small"
                                                        sx={{
                                                          fontWeight: "bold",
                                                        }}
                                                      />
                                                      {(parsed.summary?.key ||
                                                        parsed.resultKey) && (
                                                        <Typography
                                                          variant="caption"
                                                          color="text.secondary"
                                                        >
                                                          Key:{" "}
                                                          {parsed.summary
                                                            ?.key ||
                                                            parsed.resultKey}
                                                        </Typography>
                                                      )}
                                                      {parsed.idKyc && (
                                                        <Box
                                                          sx={{
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                            gap: 0.5,
                                                          }}
                                                        >
                                                          <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                          >
                                                            ID KYC:
                                                          </Typography>
                                                          <Typography
                                                            component="a"
                                                            href={`https://web.idologylive.com/reports/query.php?id=${parsed.idKyc}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            variant="caption"
                                                            sx={{
                                                              fontFamily:
                                                                "monospace",
                                                              fontWeight:
                                                                "bold",
                                                              color:
                                                                "primary.main",
                                                              textDecoration:
                                                                "underline",
                                                              cursor: "pointer",
                                                            }}
                                                          >
                                                            {parsed.idKyc}
                                                          </Typography>
                                                        </Box>
                                                      )}
                                                    </Box>

                                                    {/* Restriction Alert */}
                                                    {(parsed.restriction
                                                      ?.message ||
                                                      parsed.restriction?.key ||
                                                      (parsed.restriction
                                                        ?.matches &&
                                                        parsed.restriction
                                                          .matches.length >
                                                          0)) && (
                                                      <Box
                                                        sx={{
                                                          p: 1,
                                                          bgcolor: "#ffebee",
                                                          border:
                                                            "1px solid #ef9a9a",
                                                          borderRadius: 1,
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="caption"
                                                          fontWeight="bold"
                                                          color="error"
                                                          sx={{
                                                            display: "block",
                                                            mb: 0.75,
                                                            textTransform:
                                                              "uppercase",
                                                            letterSpacing:
                                                              "0.5px",
                                                          }}
                                                        >
                                                          Restriction Alert
                                                        </Typography>
                                                        {(parsed.restriction
                                                          ?.message ||
                                                          parsed.restriction
                                                            ?.key) && (
                                                          <Box
                                                            sx={{ mb: 0.75 }}
                                                          >
                                                            {parsed.restriction
                                                              ?.message && (
                                                              <Typography
                                                                variant="caption"
                                                                sx={{
                                                                  display:
                                                                    "block",
                                                                  fontWeight:
                                                                    "bold",
                                                                  color:
                                                                    "#c62828",
                                                                }}
                                                              >
                                                                {
                                                                  parsed
                                                                    .restriction
                                                                    .message
                                                                }
                                                              </Typography>
                                                            )}
                                                            {parsed.restriction
                                                              ?.key && (
                                                              <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                  fontSize:
                                                                    "0.65rem",
                                                                }}
                                                              >
                                                                {
                                                                  parsed
                                                                    .restriction
                                                                    .key
                                                                }
                                                              </Typography>
                                                            )}
                                                          </Box>
                                                        )}

                                                        <Stack spacing={0.5}>
                                                          {parsed.restriction?.matches?.map(
                                                            (match, idx) => (
                                                              <Box
                                                                key={idx}
                                                                sx={{
                                                                  p: 0.75,
                                                                  bgcolor:
                                                                    isPendingPass
                                                                      ? "#e8f5e9"
                                                                      : "#fff8e1",
                                                                  border:
                                                                    isPendingPass
                                                                      ? "1px solid #81c784"
                                                                      : "1px solid #ffcc80",
                                                                  borderRadius: 0.75,
                                                                  display:
                                                                    "flex",
                                                                  alignItems:
                                                                    "center",
                                                                  justifyContent:
                                                                    "space-between",
                                                                  gap: 1,
                                                                  flexWrap:
                                                                    "wrap",
                                                                }}
                                                              >
                                                                <Typography
                                                                  variant="caption"
                                                                  sx={{
                                                                    fontWeight:
                                                                      "bold",
                                                                  }}
                                                                >
                                                                  {match.list ||
                                                                    "Watchlist Match"}
                                                                </Typography>
                                                                <Box
                                                                  sx={{
                                                                    display:
                                                                      "flex",
                                                                    alignItems:
                                                                      "center",
                                                                    gap: 0.5,
                                                                    flexWrap:
                                                                      "wrap",
                                                                  }}
                                                                >
                                                                  {match.score !=
                                                                    null && (
                                                                    <Chip
                                                                      size="small"
                                                                      color={
                                                                        isPendingPass
                                                                          ? "success"
                                                                          : "warning"
                                                                      }
                                                                      label={`Score ${match.score}`}
                                                                      sx={{
                                                                        height: 18,
                                                                        fontSize:
                                                                          "0.65rem",
                                                                      }}
                                                                    />
                                                                  )}
                                                                  {match.dob && (
                                                                    <Chip
                                                                      size="small"
                                                                      variant="outlined"
                                                                      label={`DOB ${formatRestrictionDob(match.dob)}`}
                                                                      sx={{
                                                                        height: 18,
                                                                        fontSize:
                                                                          "0.65rem",
                                                                      }}
                                                                    />
                                                                  )}
                                                                </Box>
                                                              </Box>
                                                            )
                                                          )}
                                                        </Stack>
                                                      </Box>
                                                    )}

                                                    {(parsed.results?.message ||
                                                      parsed.idliveqError
                                                        ?.message) && (
                                                      <Stack
                                                        spacing={0.75}
                                                        sx={{ width: "100%" }}
                                                      >
                                                        {parsed.results
                                                          ?.message && (
                                                          <Box
                                                            sx={{
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                              gap: 0.75,
                                                              p: 0.75,
                                                              borderRadius: 1,
                                                              bgcolor:
                                                                isPendingPass
                                                                  ? "#e8f5e9"
                                                                  : "#fff3e0",
                                                              border:
                                                                isPendingPass
                                                                  ? "1px solid #81c784"
                                                                  : "1px solid #ffcc80",
                                                            }}
                                                          >
                                                            <Chip
                                                              label={
                                                                entry.action ===
                                                                "SCAN_PENDING_PASS"
                                                                  ? "ID_RESULTS"
                                                                  : "Result"
                                                              }
                                                              size="small"
                                                              color={
                                                                isPendingPass
                                                                  ? "success"
                                                                  : "warning"
                                                              }
                                                              sx={{
                                                                height: 18,
                                                                fontSize:
                                                                  "0.65rem",
                                                                fontWeight:
                                                                  "bold",
                                                              }}
                                                            />
                                                            <Typography
                                                              variant="caption"
                                                              sx={{ flex: 1 }}
                                                            >
                                                              {
                                                                parsed.results
                                                                  .message
                                                              }
                                                            </Typography>
                                                            {parsed.results
                                                              ?.key && (
                                                              <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                  fontSize:
                                                                    "0.65rem",
                                                                }}
                                                              >
                                                                {
                                                                  parsed.results
                                                                    .key
                                                                }
                                                              </Typography>
                                                            )}
                                                          </Box>
                                                        )}
                                                        {parsed.idliveqError
                                                          ?.message && (
                                                          <Box
                                                            sx={{
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                              gap: 0.75,
                                                              p: 0.75,
                                                              borderRadius: 1,
                                                              bgcolor:
                                                                isPendingPass
                                                                  ? "#e8f5e9"
                                                                  : "#ffecb3",
                                                              border:
                                                                isPendingPass
                                                                  ? "1px solid #81c784"
                                                                  : "1px solid #ffb74d",
                                                            }}
                                                          >
                                                            <Chip
                                                              label="IDLiveQ"
                                                              size="small"
                                                              color={
                                                                isPendingPass
                                                                  ? "success"
                                                                  : "warning"
                                                              }
                                                              sx={{
                                                                height: 18,
                                                                fontSize:
                                                                  "0.65rem",
                                                                fontWeight:
                                                                  "bold",
                                                              }}
                                                            />
                                                            <Typography
                                                              variant="caption"
                                                              sx={{ flex: 1 }}
                                                            >
                                                              {
                                                                parsed
                                                                  .idliveqError
                                                                  .message
                                                              }
                                                            </Typography>
                                                            {parsed.idliveqError
                                                              ?.key && (
                                                              <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                  fontSize:
                                                                    "0.65rem",
                                                                }}
                                                              >
                                                                {
                                                                  parsed
                                                                    .idliveqError
                                                                    .key
                                                                }
                                                              </Typography>
                                                            )}
                                                          </Box>
                                                        )}
                                                      </Stack>
                                                    )}
                                                  </Box>
                                                </Box>

                                                {parsed.idScan && (
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: "block" }}
                                                  >
                                                    Scan Availability:{" "}
                                                    {parsed.idScan}
                                                  </Typography>
                                                )}

                                                {/* Qualifiers */}
                                                {parsed.qualifiers.length >
                                                  0 && (
                                                  <Box>
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                      sx={{
                                                        fontSize: "0.7rem",
                                                        fontWeight: "bold",
                                                        textTransform:
                                                          "uppercase",
                                                        letterSpacing: "0.5px",
                                                      }}
                                                    >
                                                      {entry.action ===
                                                      "SCAN_PENDING_PASS"
                                                        ? "Details Found"
                                                        : "⚠ Findings"}
                                                    </Typography>
                                                    <Stack spacing={0.75}>
                                                      {parsed.qualifiers.map(
                                                        (qual, qIdx) => (
                                                          <Box
                                                            key={qIdx}
                                                            sx={{
                                                              p: 0.75,
                                                              bgcolor:
                                                                isPendingPass
                                                                  ? "#e8f5e9"
                                                                  : "#fff9c4",
                                                              border:
                                                                isPendingPass
                                                                  ? "1px solid #81c784"
                                                                  : "1px solid #fbc02d",
                                                              borderRadius: 0.5,
                                                            }}
                                                          >
                                                            <Typography
                                                              variant="caption"
                                                              sx={{
                                                                display:
                                                                  "block",
                                                                fontWeight:
                                                                  "bold",
                                                                color:
                                                                  isPendingPass
                                                                    ? "#2e7d32"
                                                                    : "#f57f17",
                                                              }}
                                                            >
                                                              {qual.message}
                                                            </Typography>
                                                            <Typography
                                                              variant="caption"
                                                              color="text.secondary"
                                                              sx={{
                                                                fontSize:
                                                                  "0.65rem",
                                                              }}
                                                            >
                                                              {qual.key}
                                                            </Typography>
                                                          </Box>
                                                        )
                                                      )}
                                                    </Stack>
                                                  </Box>
                                                )}

                                                {shouldShowReason && (
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: "block" }}
                                                  >
                                                    {parsed.reason}
                                                  </Typography>
                                                )}

                                                {parsed.source && (
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                      display: "block",
                                                      fontStyle: "italic",
                                                    }}
                                                  >
                                                    Source: {parsed.source}
                                                  </Typography>
                                                )}
                                              </Stack>
                                            </Paper>
                                          );
                                        })()
                                      ) : entry.action ===
                                        "SCAN_PENDING_PASS" ? (
                                        (() => {
                                          const parsed =
                                            parseStructuredScanPending(entry) ||
                                            parseScanPendingPass(entry.notes);
                                          if (!parsed) {
                                            return (
                                              <Paper
                                                variant="outlined"
                                                sx={{
                                                  p: 1,
                                                  mt: 0.5,
                                                  bgcolor: "#fdfdfd",
                                                }}
                                              >
                                                <Typography variant="caption">
                                                  {entry.notes}
                                                </Typography>
                                              </Paper>
                                            );
                                          }
                                          return (
                                            <Paper
                                              variant="outlined"
                                              sx={{
                                                p: 2,
                                                mt: 0.5,
                                                bgcolor: "#e8f5e9",
                                                border: "2px solid #81c784",
                                                borderRadius: 2,
                                                background:
                                                  "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                                                boxShadow:
                                                  "0 2px 8px rgba(129, 199, 132, 0.3)",
                                              }}
                                            >
                                              <Stack spacing={1.5}>
                                                {/* Status Badge */}
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                  }}
                                                >
                                                  <Chip
                                                    label={parsed.resultMessage}
                                                    color="success"
                                                    size="small"
                                                    sx={{
                                                      fontWeight: "bold",
                                                    }}
                                                  />
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                  >
                                                    Key: {parsed.resultKey}
                                                  </Typography>
                                                </Box>

                                                {/* ID KYC */}
                                                {parsed.idKyc && (
                                                  <Box>
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                      sx={{
                                                        fontSize: "0.7rem",
                                                      }}
                                                    >
                                                      ID KYC
                                                    </Typography>
                                                    <Typography
                                                      variant="body2"
                                                      sx={{
                                                        fontFamily: "monospace",
                                                        fontWeight: "bold",
                                                        color: "#2e7d32",
                                                      }}
                                                    >
                                                      {parsed.idKyc}
                                                    </Typography>
                                                  </Box>
                                                )}

                                                {parsed.reason && (
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: "block" }}
                                                  >
                                                    {parsed.reason}
                                                  </Typography>
                                                )}
                                              </Stack>
                                            </Paper>
                                          );
                                        })()
                                      ) : entry.action === "FAILED" ? (
                                        (() => {
                                          const parsed = parseFailedKycCheck(
                                            entry.notes
                                          );
                                          if (!parsed) {
                                            return (
                                              <Paper
                                                variant="outlined"
                                                sx={{
                                                  p: 1,
                                                  mt: 0.5,
                                                  bgcolor: "#fdfdfd",
                                                }}
                                              >
                                                <Typography variant="caption">
                                                  {entry.notes}
                                                </Typography>
                                              </Paper>
                                            );
                                          }
                                          return (
                                            <Paper
                                              variant="outlined"
                                              sx={{
                                                p: 2,
                                                mt: 0.5,
                                                bgcolor: "#ffebee",
                                                border: "2px solid #e57373",
                                                borderRadius: 2,
                                                background:
                                                  "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
                                                boxShadow:
                                                  "0 2px 8px rgba(229, 115, 115, 0.3)",
                                              }}
                                            >
                                              <Stack spacing={1.5}>
                                                {/* Error Message & Status */}
                                                <Box>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      display: "block",
                                                      fontWeight: "bold",
                                                      textTransform:
                                                        "uppercase",
                                                      letterSpacing: "0.5px",
                                                      color: "#c62828",
                                                      mb: 0.5,
                                                    }}
                                                  >
                                                    ✗ {parsed.errorMessage}
                                                  </Typography>
                                                  <Chip
                                                    label={
                                                      parsed.summaryMessage
                                                    }
                                                    color="error"
                                                    size="small"
                                                    sx={{
                                                      fontWeight: "bold",
                                                    }}
                                                  />
                                                </Box>

                                                {/* ID KYC */}
                                                {parsed.idKyc && (
                                                  <Box>
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                      sx={{
                                                        fontSize: "0.7rem",
                                                      }}
                                                    >
                                                      ID KYC
                                                    </Typography>
                                                    <Typography
                                                      variant="body2"
                                                      sx={{
                                                        fontFamily: "monospace",
                                                        fontWeight: "bold",
                                                        color: "#c62828",
                                                      }}
                                                    >
                                                      {parsed.idKyc}
                                                    </Typography>
                                                  </Box>
                                                )}

                                                {/* Results */}
                                                {parsed.resultsMessage && (
                                                  <Box
                                                    sx={{
                                                      p: 1,
                                                      bgcolor: "#ffcdd2",
                                                      border:
                                                        "1px solid #ef5350",
                                                      borderRadius: 0.5,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="caption"
                                                      sx={{
                                                        display: "block",
                                                        fontWeight: "bold",
                                                        color: "#b71c1c",
                                                      }}
                                                    >
                                                      {parsed.resultsMessage}
                                                    </Typography>
                                                  </Box>
                                                )}

                                                {/* ID Live Q Error */}
                                                {parsed.idLiveQMessage && (
                                                  <Box
                                                    sx={{
                                                      p: 1,
                                                      bgcolor: "#ffcdd2",
                                                      border:
                                                        "1px solid #ef5350",
                                                      borderRadius: 0.5,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="caption"
                                                      sx={{
                                                        display: "block",
                                                        fontWeight: "bold",
                                                        color: "#b71c1c",
                                                      }}
                                                    >
                                                      {parsed.idLiveQMessage}
                                                    </Typography>
                                                  </Box>
                                                )}
                                              </Stack>
                                            </Paper>
                                          );
                                        })()
                                      ) : entry.action ===
                                        "Automatic KYC Result" ? (
                                        (() => {
                                          const parsed =
                                            parseAutomaticKycResult(
                                              entry.notes
                                            );
                                          if (!parsed) {
                                            return (
                                              <Paper
                                                variant="outlined"
                                                sx={{
                                                  p: 1,
                                                  mt: 0.5,
                                                  bgcolor: "#fdfdfd",
                                                }}
                                              >
                                                <Typography variant="caption">
                                                  {entry.notes}
                                                </Typography>
                                              </Paper>
                                            );
                                          }
                                          return (
                                            <Paper
                                              variant="outlined"
                                              sx={{
                                                p: 2,
                                                mt: 0.5,
                                                bgcolor: "#ffebee",
                                                border: "2px solid #e57373",
                                                borderRadius: 2,
                                                background:
                                                  "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
                                                boxShadow:
                                                  "0 2px 8px rgba(229, 115, 115, 0.3)",
                                              }}
                                            >
                                              <Stack spacing={1.5}>
                                                {/* Error Message & Status */}
                                                <Box>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      display: "block",
                                                      fontWeight: "bold",
                                                      textTransform:
                                                        "uppercase",
                                                      letterSpacing: "0.5px",
                                                      color: "#c62828",
                                                      mb: 0.5,
                                                    }}
                                                  >
                                                    ⚡ {parsed.errorMessage}
                                                  </Typography>
                                                  <Chip
                                                    label={
                                                      parsed.summaryMessage
                                                    }
                                                    color="error"
                                                    size="small"
                                                    sx={{
                                                      fontWeight: "bold",
                                                    }}
                                                  />
                                                </Box>

                                                {/* ID KYC */}
                                                {parsed.idKyc && (
                                                  <Box>
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                      sx={{
                                                        fontSize: "0.7rem",
                                                      }}
                                                    >
                                                      ID KYC
                                                    </Typography>
                                                    <Typography
                                                      variant="body2"
                                                      sx={{
                                                        fontFamily: "monospace",
                                                        fontWeight: "bold",
                                                        color: "#c62828",
                                                      }}
                                                    >
                                                      {parsed.idKyc}
                                                    </Typography>
                                                  </Box>
                                                )}

                                                {/* Results */}
                                                {parsed.resultsMessage && (
                                                  <Box
                                                    sx={{
                                                      p: 1,
                                                      bgcolor: "#ffcdd2",
                                                      border:
                                                        "1px solid #ef5350",
                                                      borderRadius: 0.5,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="caption"
                                                      sx={{
                                                        display: "block",
                                                        fontWeight: "bold",
                                                        color: "#b71c1c",
                                                      }}
                                                    >
                                                      {parsed.resultsMessage}
                                                    </Typography>
                                                  </Box>
                                                )}

                                                {/* ID Live Q Error */}
                                                {parsed.idLiveQMessage && (
                                                  <Box
                                                    sx={{
                                                      p: 1,
                                                      bgcolor: "#ffcdd2",
                                                      border:
                                                        "1px solid #ef5350",
                                                      borderRadius: 0.5,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="caption"
                                                      sx={{
                                                        display: "block",
                                                        fontWeight: "bold",
                                                        color: "#b71c1c",
                                                      }}
                                                    >
                                                      {parsed.idLiveQMessage}
                                                    </Typography>
                                                  </Box>
                                                )}
                                              </Stack>
                                            </Paper>
                                          );
                                        })()
                                      ) : (
                                        <Paper
                                          variant="outlined"
                                          sx={{
                                            p: 1,
                                            mt: 0.5,
                                            bgcolor: "#fdfdfd",
                                          }}
                                        >
                                          <Typography variant="caption">
                                            {entry.notes}
                                          </Typography>
                                        </Paper>
                                      )}
                                    </>
                                  )}
                                  {entry.requestedDocuments && (
                                    <Typography
                                      variant="caption"
                                      color="info.main"
                                      sx={{ display: "block", mt: 0.5 }}
                                    >
                                      <strong>Requested:</strong>{" "}
                                      {entry.requestedDocuments}
                                    </Typography>
                                  )}
                                </Box>
                              )
                            )
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontStyle: "italic" }}
                            >
                              No audit history found.
                            </Typography>
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
        </Box>
        <Snackbar
          open={feedback.open}
          autoHideDuration={6000}
          onClose={handleFeedbackClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleFeedbackClose}
            severity={feedback.type}
            sx={{ width: "100%" }}
          >
            {feedback.message}
          </Alert>
        </Snackbar>
      </Dialog>

      {/* ── KYC Document Preview — rendered outside the main Dialog to avoid z-index issues ── */}
      <Dialog
        open={!!previewDoc}
        onClose={closePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            {previewDoc
              ? (DOCUMENT_TYPE_LABELS[previewDoc.documentType] ||
                  previewDoc.documentType) +
                " — " +
                previewDoc.side
              : ""}
          </Box>
          <IconButton size="small" onClick={closePreview}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {previewSrc && previewFileName && (
          <Box
            sx={{ px: 3, pt: 1, display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const link = document.createElement("a");
                link.href = previewDownloadSrc || previewSrc;
                link.download = previewFileName;
                document.body.appendChild(link);
                link.click();
                link.remove();
              }}
            >
              Download
            </Button>
          </Box>
        )}
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            pb: 3,
            minHeight: 200,
          }}
        >
          {previewLoading && <CircularProgress sx={{ mt: 4 }} />}
          {previewError && <Alert severity="error">{previewError}</Alert>}
          {previewSrc &&
            !previewLoading &&
            !previewError &&
            ((previewDoc?.mimeType || "").startsWith("image/") ? (
              <img
                src={previewSrc}
                alt="KYC Document"
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            ) : (
              <iframe
                src={previewSrc}
                title="KYC Document"
                style={{ width: "100%", height: "70vh", border: "none" }}
              />
            ))}
        </DialogContent>
      </Dialog>

      {/* ── KYC Review Dialog ── */}
      <Dialog
        open={kycReviewOpen}
        onClose={() => !kycReviewMutation.isLoading && setKycReviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}
        >
          <WarningAmberIcon sx={{ color: "warning.main" }} />
          {Labels.getLabel(
            "memberSecureInfo.reviewKycTitle",
            "Review KYC Verification"
          )}
          <IconButton
            size="small"
            onClick={() => setKycReviewOpen(false)}
            disabled={kycReviewMutation.isLoading}
            sx={{ ml: "auto" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {Labels.getLabel(
              "memberSecureInfo.reviewKycBody",
              "The automated KYC check for this member has FAILED. Please review the documents and select an action."
            )}
          </Alert>
          <Stack spacing={2}>
            {/* Override to PASSED */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderColor: "success.main",
                borderRadius: 2,
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
              }}
            >
              <CheckCircleOutlineIcon sx={{ color: "success.main", mt: 0.3 }} />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle2"
                  color="success.dark"
                  gutterBottom
                >
                  {Labels.getLabel(
                    "memberSecureInfo.reviewKycApproveHint",
                    "Override to PASSED"
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Labels.getLabel(
                    "memberSecureInfo.reviewKycApproveHintBody",
                    "The workflow will resume and the card will be issued to this member."
                  )}
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="success"
                size="small"
                disabled={
                  kycReviewMutation.isLoading ||
                  !approveNotes.trim() ||
                  !approveTier
                }
                startIcon={<CheckCircleOutlineIcon />}
                onClick={() =>
                  kycReviewMutation.mutate({
                    customerId:
                      secureInfoData?.customerId || memberInfo?.customerId,
                    companyId: memberInfo?.companyId,
                    decision: "APPROVED",
                    reviewedBy: UserService.getUsername() || "admin",
                    notes: approveNotes,
                    tier: approveTier,
                  })
                }
                sx={{ whiteSpace: "nowrap", alignSelf: "center" }}
              >
                {Labels.getLabel(
                  "memberSecureInfo.overridePassed",
                  "Override to PASSED"
                )}
              </Button>
            </Paper>
            <Box sx={{ mt: 1 }}>
              <TextField
                label="Approval Notes (required)"
                multiline
                rows={2}
                fullWidth
                size="small"
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                placeholder="Document why this case is being manually overridden to PASSED."
              />
            </Box>
            <Box sx={{ mt: 1 }}>
              <TextField
                select
                label="Tier (required)"
                fullWidth
                size="small"
                value={approveTier}
                onChange={(e) => setApproveTier(e.target.value)}
              >
                {[1, 2, 3, 4].map((tier) => (
                  <MenuItem key={tier} value={`TIER_${tier}`}>
                    Tier {tier}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            {/* Request More Info */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderColor: "info.main",
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <DescriptionIcon sx={{ color: "info.main", mt: 0.3 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    color="info.dark"
                    gutterBottom
                  >
                    {Labels.getLabel(
                      "memberSecureInfo.reviewKycRequestInfoHint",
                      "Request More Info"
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {Labels.getLabel(
                      "memberSecureInfo.reviewKycRequestInfoHintBody",
                      "The workflow will pause until the member uploads the requested documents."
                    )}
                  </Typography>
                </Box>
                <Button
                  variant={requestInfoMode ? "contained" : "outlined"}
                  color="info"
                  size="small"
                  disabled={kycReviewMutation.isLoading}
                  startIcon={<EditNoteIcon />}
                  onClick={() => setRequestInfoMode(!requestInfoMode)}
                  sx={{ whiteSpace: "nowrap", alignSelf: "center" }}
                >
                  {Labels.getLabel(
                    "memberSecureInfo.requestDocs",
                    "Request Docs"
                  )}
                </Button>
              </Box>

              <Collapse in={requestInfoMode}>
                <Divider sx={{ my: 1 }} />
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="requested-docs-label">
                      {Labels.getLabel(
                        "memberSecureInfo.requestedDocuments",
                        "Requested Documents"
                      )}
                    </InputLabel>
                    <Select
                      labelId="requested-docs-label"
                      multiple
                      value={requestedDocs}
                      onChange={(e) => {
                        const { value } = e.target;
                        setRequestedDocs(
                          typeof value === "string" ? value.split(",") : value
                        );
                      }}
                      renderValue={(selected) =>
                        selected
                          .map(
                            (val) =>
                              ALLOWABLE_DOCUMENTS.find((d) => d.value === val)
                                ?.label || val
                          )
                          .join(", ")
                      }
                      label={Labels.getLabel(
                        "memberSecureInfo.requestedDocuments",
                        "Requested Documents"
                      )}
                    >
                      {ALLOWABLE_DOCUMENTS.map((doc) => (
                        <MenuItem key={doc.value} value={doc.value}>
                          <Checkbox
                            checked={requestedDocs.indexOf(doc.value) > -1}
                          />
                          <ListItemText primary={doc.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label={Labels.getLabel(
                      "memberSecureInfo.requestNotes",
                      "Notes for Member"
                    )}
                    multiline
                    rows={3}
                    fullWidth
                    size="small"
                    value={requestNotes}
                    onChange={(e) => setRequestNotes(e.target.value)}
                    placeholder="E.g., Please upload a clearer picture of your ID."
                  />

                  <TextField
                    label="Manual Evidence / Additional Documents"
                    multiline
                    rows={2}
                    fullWidth
                    size="small"
                    value={requestExtraEvidence}
                    onChange={(e) => setRequestExtraEvidence(e.target.value)}
                    placeholder="Add extra requested evidence (comma, semicolon, or newline separated)."
                  />

                  <Button
                    variant="contained"
                    color="info"
                    fullWidth
                    disabled={
                      requestedDocs.length === 0 || kycReviewMutation.isLoading
                    }
                    onClick={() =>
                      kycReviewMutation.mutate({
                        customerId:
                          secureInfoData?.customerId || memberInfo?.customerId,
                        companyId: memberInfo?.companyId,
                        decision: "REQUEST_INFO",
                        reviewedBy: UserService.getUsername() || "admin",
                        notes: requestNotes,
                        requestedDocuments: buildRequestedDocumentsPayload(
                          requestedDocs,
                          requestExtraEvidence
                        ),
                      })
                    }
                  >
                    {Labels.getLabel(
                      "memberSecureInfo.submitRequest",
                      "Submit Request"
                    )}
                  </Button>
                </Stack>
              </Collapse>
            </Paper>
            {/* Confirm FAILED */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderColor: "error.main",
                borderRadius: 2,
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
              }}
            >
              <BlockIcon sx={{ color: "error.main", mt: 0.3 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="error.dark" gutterBottom>
                  {Labels.getLabel(
                    "memberSecureInfo.reviewKycRejectHint",
                    "Confirm FAILED"
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Labels.getLabel(
                    "memberSecureInfo.reviewKycRejectHintBody",
                    "The workflow will finalize as KYC_REJECTED. All member data is preserved."
                  )}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={kycReviewMutation.isLoading}
                startIcon={<BlockIcon />}
                onClick={() =>
                  kycReviewMutation.mutate({
                    customerId:
                      secureInfoData?.customerId || memberInfo?.customerId,
                    companyId: memberInfo?.companyId,
                    decision: "REJECTED",
                    reviewedBy: UserService.getUsername() || "admin",
                    notes: rejectNotes,
                  })
                }
                sx={{ whiteSpace: "nowrap", alignSelf: "center" }}
              >
                {Labels.getLabel(
                  "memberSecureInfo.confirmFailed",
                  "Confirm FAILED"
                )}
              </Button>
            </Paper>
            <Box sx={{ mt: 1 }}>
              <TextField
                label="Rejection Notes"
                multiline
                rows={2}
                fullWidth
                size="small"
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                placeholder="Add compliance rationale for this rejection."
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setKycReviewOpen(false)}
            disabled={kycReviewMutation.isLoading}
            color="inherit"
          >
            {Labels.getLabel("common.cancel", "Cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={kycUploadDialogOpen}
        onClose={() =>
          !kycDocumentUploadMutation.isLoading && setKycUploadDialogOpen(false)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <UploadFileIcon sx={{ color: "secondary.main" }} />
          Upload KYC Document
          <IconButton
            size="small"
            onClick={() => setKycUploadDialogOpen(false)}
            disabled={kycDocumentUploadMutation.isLoading}
            sx={{ ml: "auto" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload image or PDF documents here. This is separate from the Review
            KYC decision actions.
          </Typography>

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="docs-upload-doc-type-label">
                  Document Type
                </InputLabel>
                <Select
                  labelId="docs-upload-doc-type-label"
                  value={reviewUploadDocType}
                  label="Document Type"
                  onChange={(e) => setReviewUploadDocType(e.target.value)}
                >
                  {ALLOWABLE_DOCUMENTS.map((doc) => (
                    <MenuItem key={doc.value} value={doc.value}>
                      {doc.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="docs-upload-doc-side-label">Side</InputLabel>
                <Select
                  labelId="docs-upload-doc-side-label"
                  value={reviewUploadSide}
                  label="Side"
                  onChange={(e) => setReviewUploadSide(e.target.value)}
                >
                  {ALLOWABLE_DOCUMENT_SIDES.map((side) => (
                    <MenuItem key={side} value={side}>
                      {side}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Country Code"
                size="small"
                fullWidth
                value={reviewUploadCountryCode}
                onChange={(e) => setReviewUploadCountryCode(e.target.value)}
                placeholder="US"
              />
            </Grid>
          </Grid>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ mt: 2 }}
          >
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              disabled={kycDocumentUploadMutation.isLoading}
            >
              Choose File
              <input
                key={reviewUploadInputKey}
                hidden
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={(e) =>
                  setReviewUploadFile(e.target.files?.[0] || null)
                }
              />
            </Button>

            <Button
              variant="contained"
              color="secondary"
              disabled={
                !reviewUploadFile || kycDocumentUploadMutation.isLoading
              }
              onClick={handleUploadReviewDocument}
            >
              {kycDocumentUploadMutation.isLoading
                ? "Uploading..."
                : "Upload Document"}
            </Button>

            {reviewUploadFile && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ alignSelf: "center" }}
              >
                {reviewUploadFile.name}
              </Typography>
            )}
          </Stack>

          {uploadedReviewDocs.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Uploaded in this session:
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                sx={{ mt: 0.5, gap: 1 }}
              >
                {uploadedReviewDocs.map((doc, idx) => (
                  <Chip
                    key={`${doc.id}-${idx}`}
                    label={`${doc.documentType} - ${doc.originalFileName || "document"}`}
                    size="small"
                    onDelete={() => removeUploadedReviewDoc(idx)}
                    deleteIcon={<DeleteOutlineIcon />}
                    color="secondary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setKycUploadDialogOpen(false)}
            disabled={kycDocumentUploadMutation.isLoading}
            color="inherit"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
